/**
 * @file gameStore.ts
 * @description Zustand store for managing active game session state.
 * Handles scenes, characters, narrative, and game-specific chat.
 */

import { create } from 'zustand';
import { subscribeWithSelector, persist, createJSONStorage } from 'zustand/middleware';
import type { StateStorage } from 'zustand/middleware';
import type {
  GameSceneInfo,
  ServerMessage,
  GameCharacterInfo,
  EncounterType,
  WireConditionEntry,
  CharacterStateChange,
  CharacterPermanent,
  CharacterSession,
  RoundResultMessage,
  ObservableCharacterView,
  HealthState,
  SessionEndReason,
  InGameTimeData,
  SceneItem,
  SceneInteractable,
  TravelDestination,
  ActiveBoon,
  ClassName,
  CampaignJournal,
  RejectedIntent,
  ResourceRequirement,
  LastSavingThrow,
  PresentationRound,
  EntityPresentationState,
} from '@danda/shared';
import {
  advancePresentationWatermark,
  presentationWatermarkAfterGameState,
  rollbackPresentationWatermarkForFailedRound,
  shouldStashPresentationRound,
  worldSnapshotFromGameState,
} from '@/world/presentation-sync';
import { PREPARED_CASTER_CLASSES } from '@danda/shared';
import { GAME_MASTER, MASTER_KIRA } from '@danda/shared/constants/narrator';
import { pickChatBubbleColor } from '@danda/shared/constants/chat-colors';
import { notificationService } from '@/lib/notifications';
import { handleGameAudioMessage } from '@/lib/audio/audio-routing';
import { toast } from '@/lib/toast';
import { formatSceneName } from '@/lib/utils';
import type { ChatMessageType } from '@/lib/markdown';

// =============================================================================
// Connection Status State Machine
// =============================================================================

export type GameConnectionStatus =
  | { status: 'idle' }
  | { status: 'validating' }
  | { status: 'connecting' }
  | { status: 'connected' }
  | { status: 'reconnecting'; attempt: number }
  | { status: 'disconnected' } // all retries exhausted — show overlay
  | { status: 'ending'; reason?: string } // session ending — show banner
  | { status: 'ended'; reason: SessionEndReason }; // session ended — will redirect

// _CharacterStateChange removed — superseded by CharacterStateChange from @danda/shared

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get condition name from WireConditionEntry
 * Handles both string conditions and object conditions with duration metadata
 */
function getConditionName(condition: WireConditionEntry): string {
  return typeof condition === 'string' ? condition : condition.name;
}

/**
 * Derive a HealthState from HP ratio and conditions.
 * Follows D&D 5E observable health categories.
 * Checks for "dead" condition before HP-based derivation to avoid showing "dying" for dead characters.
 */
export function deriveHealthState(
  hp: number,
  maxHp: number,
  conditions?: WireConditionEntry[],
  isDead?: boolean
): HealthState {
  // Backend-authoritative dead flag wins over HP/condition derivation.
  if (isDead) return 'dead';
  // Check for dead condition first — HP=0 with dead condition is dead, not dying
  if (conditions?.some((c) => getConditionName(c) === 'dead')) return 'dead';
  if (hp <= 0) return maxHp > 0 ? 'dying' : 'dead';
  const pct = (hp / Math.max(maxHp, 1)) * 100;
  if (pct >= 100) return 'uninjured';
  if (pct >= 50) return 'wounded';
  if (pct >= 25) return 'bloodied';
  return 'critical';
}

/**
 * Conditions the health badge already states, so a card must not repeat them.
 *
 * Only DYING and DEAD speak for `dying`/`unconscious`. `deriveHealthState` has
 * no unconscious tier, so a member at full HP whose only condition is
 * `unconscious` resolves to `uninjured`. Dropping the badge unconditionally
 * then left nothing on the card at all, and a Sleep'd or Hideous Laughter'd
 * teammate read as perfectly fine. `dead` is always safe to drop: every path
 * that produces it resolves to the DEAD tier.
 *
 * Mirrors mobile's `conditionsStatedByHealthPill`. Keep the two in step.
 */
export function conditionsStatedByHealthBadge(healthState: HealthState): Set<string> {
  return healthState === 'dying' || healthState === 'dead'
    ? new Set(['dead', 'dying', 'unconscious'])
    : new Set(['dead']);
}

/** Conditions to render beside the health badge, with the redundant ones removed. */
export function visibleConditions(
  conditions: WireConditionEntry[] | undefined,
  healthState: HealthState
): WireConditionEntry[] {
  const stated = conditionsStatedByHealthBadge(healthState);
  return (conditions ?? []).filter((c) => !stated.has(getConditionName(c).toLowerCase()));
}

// mergeConditions removed — condition merge logic inlined in applyCharacterStateChange

// _applyConditionChanges removed — condition changes handled directly in applyCharacterStateChange

/**
 * The `Disposition` an attitude collapses to over the observable wire.
 * Mirrors the server's `From<NpcAttitude> for Disposition`.
 */
function dispositionOf(
  attitude: NonNullable<GameCharacterInfo['attitude']>
): ObservableCharacterView['disposition'] {
  switch (attitude) {
    case 'hostile':
      return 'hostile';
    case 'friendly':
    case 'allied':
      return 'friendly';
    case 'unfriendly':
    case 'neutral':
      return 'neutral';
  }
}

/**
 * Fold this round's observation onto a character we already hold.
 *
 * `attitude` needs care because the observation can carry it at two precisions.
 * `obs.attitude` is the server's exact stored value and is authoritative when
 * present. `obs.disposition` is the three-value read the engine reasons over,
 * derived lossily: `unfriendly` folds onto `neutral`, `allied` onto `friendly`.
 * Spreading the coarse one over the precise stored value erased both
 * distinctions one round after connect, and the target pickers gate on exactly
 * them — an `unfriendly` NPC left `buildEnemyOptions` and entered the buff list
 * on the same tick.
 *
 * Falling back to `disposition` cannot resolve that on its own: a real attitude
 * change WITHIN a bucket (the talked-down guard going `unfriendly` to
 * `neutral`) produces no disagreement, so the stored value would pin. Hence the
 * precise field.
 *
 * The attitudes this protocol does not model (`frightened`, `suspicious`,
 * `indifferent`, `unknown`) arrive AS `neutral` rather than being omitted, so
 * they take the exact-value branch and correctly displace a stored
 * `unfriendly`. The fallback covers the case where the server holds no attitude
 * at all and only a disposition reaches us.
 */
function mergeObservedChar(
  existing: GameCharacterInfo,
  obs: ObservableCharacterView
): GameCharacterInfo {
  const fresh = observableViewToGameCharInfo(obs);
  const seen = obs.disposition;
  const attitude =
    obs.attitude ??
    (seen === undefined || (existing.attitude && dispositionOf(existing.attitude) === seen)
      ? existing.attitude
      : fresh.attitude);
  return { ...existing, ...fresh, attitude };
}

/**
 * Convert an ObservableCharacterView to a minimal GameCharacterInfo for display.
 * Used when new characters appear via observable views (spawns, newly visible NPCs).
 * Only observable fields are populated — exact HP, spell slots, etc. are unknown.
 */
function observableViewToGameCharInfo(obs: ObservableCharacterView): GameCharacterInfo {
  // Map health state to rough HP values for display
  // These are approximations since we only have the health state category
  const hpFromHealthState: Record<string, { hp: number; maxHp: number }> = {
    uninjured: { hp: 10, maxHp: 10 },
    wounded: { hp: 7, maxHp: 10 },
    bloodied: { hp: 4, maxHp: 10 },
    critical: { hp: 1, maxHp: 10 },
    dying: { hp: 0, maxHp: 10 },
    dead: { hp: 0, maxHp: 10 },
  };
  const { hp, maxHp } = hpFromHealthState[obs.healthState] ?? { hp: 10, maxHp: 10 };

  return {
    charId: obs.id,
    name: obs.name,
    hp,
    maxHp,
    conditions: obs.visibleConditions,
    // Recovery badges for OTHER party members. Only the signed-in player's own
    // character gets a full mid-session re-sync (`syncPlayerCharData`), so
    // without these a teammate's badge kept asserting the pre-long-rest penalty
    // until reconnect, even though the backend had already reduced it.
    //
    // Assigned unconditionally, undefined included: the round_result merge is
    // `{ ...existing, ...fresh }`, so a key that is always present is what lets
    // the badge CLEAR when the penalty reaches zero (the server omits the field
    // rather than sending 0). Dropping the key on absence would pin the last
    // non-zero value forever, which is the bug being fixed.
    exhaustionLevel: obs.exhaustionLevel,
    resurrectionPenalty: obs.resurrectionPenalty,
    type: obs.charType,
    isPartyMember: obs.isPartyMember,
    level: obs.level,
    species: obs.species,
    // Authored 3D look: without this, a character entering view
    // mid-session rendered generic seeded visuals until reconnect.
    appearance: obs.appearance,
    // Creature size, for the tactical map footprint. Characters arriving via an
    // observable view carry no `permanent` blob, so without this every monster
    // that spawns after connect would render as a 1x1 token.
    size: obs.size,
    class: obs.class,
    portraitUrl: obs.portraitUrl,
    avatarUrl: obs.avatarUrl,
    avatarEmoji: obs.avatarEmoji,
    isConcentrating: obs.isConcentrating,
    concentrationSpell: obs.concentrationSpell ?? null,
    // Exact stored attitude when the server sent one, else the three-value
    // disposition it folds down to. The pickers gate on `unfriendly`, which the
    // fold erases, so a character first seen mid-session would otherwise enter
    // the roster already flattened.
    attitude: obs.attitude ?? (obs.disposition as GameCharacterInfo['attitude']),
    healthState: obs.healthState,
    // Grid position for the tactical map. Observable views have carried this all
    // along; it was simply never mapped onto the store character, so the map had
    // nothing to draw. `x`/`y` are optional on the observable wire type but the
    // backend always fills both for a positioned character, so an incomplete
    // pair means "not on the grid" and is dropped.
    position:
      obs.position?.x !== undefined && obs.position?.y !== undefined
        ? { x: obs.position.x, y: obs.position.y }
        : undefined,
    equippedItems: [
      ...(obs.equippedWeapon ? [obs.equippedWeapon] : []),
      ...(obs.equippedArmor ? [obs.equippedArmor] : []),
      ...(obs.equippedShield ? [obs.equippedShield] : []),
      ...(obs.equippedWondrousItems ?? []),
    ],
  };
}

/**
 * Apply a class-resource delta to a CharacterSession in place (mirrors the Rust
 * mapping in `backend/shared/src/types/character/session.rs::apply_state_changes`,
 * lines 800-845). Returns a NEW session object so callers can re-assign without
 * mutating the original.
 *
 * Used for two reasons:
 *  1. Server-broadcast `class_resource_used` events — applied to remote party
 *     members' panels (the player's own char is already covered by the
 *     authoritative `my_character_session` snapshot).
 *  2. Optimistic pre-decrement on click — the user clicks an ability and the
 *     counter drops immediately; the snapshot reconciles when the round result
 *     arrives.
 *
 * Resources are tracked by `<resource>_used` (delta from max). The exception
 * is `sorcery_points` which uses `sorcery_points_remaining` semantics —
 * sorcery_points_delta is applied separately by the caller.
 */
function applyClassResourceDelta(
  session: CharacterSession | undefined,
  resource: string,
  count: number,
  /**
   * Pool size for REMAINING-semantics resources, used when the wire omitted the
   * field. Only `sorcery_points` needs it today. Absent means "at max" for such
   * a pool, so defaulting the missing field to 0 would read a FULL pool as
   * empty.
   */
  fullPool?: number
): CharacterSession | undefined {
  if (!session) return session;
  const cr = { ...(session.class_resources ?? {}) };
  const bump = (key: string) => {
    const current = (cr as Record<string, unknown>)[key];
    const cur = typeof current === 'number' ? current : 0;
    (cr as Record<string, unknown>)[key] = Math.max(0, cur + count);
  };
  switch (resource) {
    case 'rage':
      bump('rage_uses');
      break;
    case 'bardic_inspiration':
      bump('bardic_inspiration_uses');
      break;
    case 'channel_divinity':
      bump('channel_divinity_uses');
      break;
    case 'wild_shape':
      bump('wild_shape_uses');
      break;
    case 'action_surge':
      bump('action_surge_uses');
      break;
    case 'second_wind':
      bump('second_wind_uses');
      break;
    case 'indomitable':
      bump('indomitable_uses');
      break;
    case 'ki':
    // Pre-2024 Ki Points → 2024 Focus Points (SRD 5.2 rename). Either
    // resource string maps to the same `focus_points` SPENT counter.
    // Falls through.
    case 'focus_points':
      // Rust `focus_points_used` is serde-renamed to `focus_points` (SPENT counter).
      bump('focus_points');
      break;
    case 'lay_on_hands_pool':
      // Rust `lay_on_hands_pool_used` is serde-renamed to `lay_on_hands_pool` (SPENT counter).
      bump('lay_on_hands_pool');
      break;
    case 'pact_magic_slots':
      bump('pact_magic_slots_used');
      break;
    case 'sorcery_points': {
      // Remaining-semantics resource (Rust `sorcery_points_remaining` is
      // serde-renamed to `sorcery_points`). Count is a signed delta — negative
      // when spent, positive when gained via Slot→SP.
      // Absent means AT MAX, not zero: `sorcery_points_remaining` is
      // `Option<i32>` with `skip_serializing_if = "Option::is_none"`, and a long
      // rest resets it to `None` (session_types.rs, short_rest.rs). Defaulting a
      // missing field to 0 made the first metamagic click compute
      // `max(0, 0 - cost) = 0`, so a sorcerer at a FULL pool watched every
      // metamagic card grey out until the next round_result re-synced them.
      const remaining = typeof cr.sorcery_points === 'number' ? cr.sorcery_points : (fullPool ?? 0);
      cr.sorcery_points = Math.max(0, remaining + count);
      break;
    }
    // Other resource names (favored_foe, natures_veil, tireless, etc.) aren't
    // wired into the typed session yet — silently ignored.
  }
  return { ...session, class_resources: cr };
}

/**
 * Apply character state changes to a GameCharacterInfo.
 * Handles round_result StateChangeMessage array format (accumulated into per-character changes).
 */
function applyCharacterStateChange(
  char: GameCharacterInfo,
  changes: CharacterStateChange
): GameCharacterInfo {
  const updated = { ...char };

  // Apply HP changes (absolute value takes precedence over delta)
  if (changes.hp !== undefined) {
    updated.hp = changes.hp;
  } else if (changes.hpChange !== undefined && char.hp !== undefined) {
    updated.hp = char.hp + changes.hpChange;
  }

  // Apply max HP change
  if (changes.maxHp !== undefined) {
    updated.maxHp = changes.maxHp;
  }

  // Apply temp HP
  if (changes.tempHp !== undefined) {
    updated.tempHp = changes.tempHp;
  }

  // Apply condition changes (preserve full WireConditionEntry objects with duration metadata)
  if (changes.conditions) {
    let conditions: WireConditionEntry[] = [...(char.conditions ?? [])];

    // Remove conditions first
    if (changes.conditions.remove && changes.conditions.remove.length > 0) {
      const toRemove = new Set(changes.conditions.remove.map((c) => c.toLowerCase()));
      conditions = conditions.filter((c) => !toRemove.has(getConditionName(c).toLowerCase()));
    }

    // Add conditions (merge by name — new entries replace existing with same name
    // to update duration metadata, otherwise append)
    if (changes.conditions.add && changes.conditions.add.length > 0) {
      for (const cond of changes.conditions.add) {
        const name = getConditionName(cond);
        // Remove existing entry with same name (so the new one replaces it with fresh metadata)
        conditions = conditions.filter(
          (c) => getConditionName(c).toLowerCase() !== name.toLowerCase()
        );
        conditions.push(cond);
      }
    }

    updated.conditions = conditions;
  }

  // Spell slots used (decrement from max)
  if (changes.spellSlotsUsed) {
    const currentUsed = { ...(char.spellSlotsUsed ?? {}) };
    for (const [level, count] of Object.entries(changes.spellSlotsUsed)) {
      const lvl = Number(level);
      currentUsed[lvl] = (currentUsed[lvl] ?? 0) + count;
    }
    updated.spellSlotsUsed = currentUsed;
  }

  if (changes.concentration !== undefined) {
    updated.isConcentrating = changes.concentration != null;
    updated.concentrationSpell = changes.concentration ?? null;
  }

  // Death saves
  if (changes.deathSave) {
    if (changes.deathSave.success) {
      const successes = (char.deathSaveSuccesses ?? 0) + (changes.deathSave.critical ? 2 : 1);
      updated.deathSaveSuccesses = Math.min(successes, 3);
    } else {
      const failures = (char.deathSaveFailures ?? 0) + (changes.deathSave.critical ? 2 : 1);
      updated.deathSaveFailures = Math.min(failures, 3);
    }
  }

  // Exhaustion
  if (changes.exhaustionLevel !== undefined) {
    updated.exhaustionLevel = changes.exhaustionLevel;
  }

  // Cover
  if (changes.cover !== undefined) {
    updated.cover = changes.cover;
  }

  // Party-panel combat badges. All absolute: assign, never accumulate. The
  // `!== undefined` guard is what distinguishes "the backend says false" (which
  // must clear the badge) from "no message this round" (leave it alone).
  if (changes.heroicInspiration !== undefined) {
    updated.heroicInspiration = changes.heroicInspiration;
  }
  if (changes.deathSaveTally !== undefined) {
    updated.deathSaveSuccesses = changes.deathSaveTally.successes;
    updated.deathSaveFailures = changes.deathSaveTally.failures;
  }
  if (changes.lastSavingThrow !== undefined) {
    updated.lastSavingThrow = changes.lastSavingThrow;
  }
  if (changes.critMarkers !== undefined) {
    updated.critDealtThisRound = changes.critMarkers.dealt;
    updated.critTakenThisRound = changes.critMarkers.taken;
  }
  // The player's own map token. Only ever sent for the recipient's own
  // character, so this never competes with the observable-view position that
  // carries everyone else.
  if (changes.gridPosition !== undefined) {
    updated.position = changes.gridPosition;
  }
  // Combat ended: every combat-scoped badge goes at once. Applied AFTER the
  // per-field assignments above so the clear wins over a same-round set, which
  // matches the Rust precedence in `CharacterSession::apply_state_changes`.
  // Without this the server clears its own state, emits the message, and the
  // client silently ignores it, leaving a teammate's pill pinned until reconnect.
  if (changes.combatBadgesCleared) {
    updated.lastSavingThrow = undefined;
    updated.cover = undefined;
    updated.critDealtThisRound = false;
    updated.critTakenThisRound = false;
    // Grid position is combat-scoped too (the server clears it on combat exit),
    // so dropping it here stops a stale token flashing on the first round of the
    // next fight, before that round's position arrives.
    updated.position = undefined;
  }

  // Re-derive healthState from updated HP + conditions
  // Monsters/animals die instantly at 0 HP (no death saves per SRD 5E)
  if (updated.hp !== undefined && updated.maxHp !== undefined) {
    const isMonster = updated.type === 'monster' || updated.type === 'animal';
    updated.healthState =
      updated.hp <= 0 && isMonster
        ? 'dead'
        : deriveHealthState(updated.hp, updated.maxHp, updated.conditions, updated.isDead);
  }

  // Items acquired
  if (changes.itemsAcquired && changes.itemsAcquired.length > 0) {
    const equipment = [...(char.equipment ?? [])];
    for (const item of changes.itemsAcquired) {
      equipment.push({
        name: item.item_name,
        item_id: item.item_id,
        item_kind: item.magical ? 'wondrous_item' : 'mundane',
        quantity: item.quantity ?? 1,
      });
    }
    updated.equipment = equipment;
    // Update session equipped_slots.wondrous_items for auto-equip (mirrors backend)
    if (updated.session) {
      const existingSlots = updated.session.equipped_slots ?? { two_handed_equipped: false };
      const wondrous = [...(existingSlots.wondrous_items ?? [])];
      for (const item of changes.itemsAcquired) {
        if (item.magical && !wondrous.some((w) => w === item.item_id)) {
          wondrous.push(item.item_id);
        }
      }
      updated.session = {
        ...updated.session,
        equipped_slots: { ...existingSlots, wondrous_items: wondrous },
      };
    }
  }

  // Items removed (itemsRemoved contains item_ids)
  if (changes.itemsRemoved && changes.itemsRemoved.length > 0) {
    const toRemove = new Set(changes.itemsRemoved);
    // Read from updated.equipment so an items_acquired block applied earlier
    // in this same delta isn't clobbered.
    updated.equipment = (updated.equipment ?? char.equipment ?? []).filter(
      (e) => !toRemove.has(e.item_id ?? '')
    );
    // Note: equippedItems (display names from observable views) are refreshed each round
    // Remove from session equipped_slots.wondrous_items if present
    if (updated.session?.equipped_slots?.wondrous_items) {
      updated.session = {
        ...updated.session,
        equipped_slots: {
          ...updated.session.equipped_slots,
          wondrous_items: updated.session.equipped_slots.wondrous_items.filter(
            (id) => !toRemove.has(id)
          ),
        },
      };
    }
  }

  // XP gained (increment total)
  if (changes.xpGained) {
    updated.xp = (char.xp ?? 0) + changes.xpGained;
  }

  // Features used (decrement uses)
  if (changes.featuresUsed && changes.featuresUsed.length > 0) {
    const featureUses = { ...(char.featureUses ?? {}) };
    for (const feature of changes.featuresUsed) {
      const current = featureUses[feature] ?? 0;
      featureUses[feature] = Math.max(0, current - 1);
    }
    updated.featureUses = featureUses;
  }

  // Class resources used — reuse the canonical resource→field mapping helper.
  // The wire delta is positive (count consumed); applyClassResourceDelta adds
  // to the *_used field. Sorcery points use the dedicated `sorcery_points_delta`
  // wire field (signed remaining semantics) — guard against any future server
  // drift that emits SP via class_resources_used: refilling SP on a "consumed"
  // event would be the inverted-sign footgun.
  if (changes.classResourcesUsed) {
    let session = updated.session;
    for (const [resource, count] of Object.entries(changes.classResourcesUsed)) {
      if (resource === 'sorcery_points') continue;
      session = applyClassResourceDelta(session, resource, count);
    }
    updated.session = session;
  }

  // Sorcery points delta — positive on Slot→SP gain, negative on SP→Slot spend.
  // Stored as "remaining" semantics in the session, so apply directly.
  if (typeof changes.sorceryPointsDelta === 'number') {
    updated.session = applyClassResourceDelta(
      updated.session,
      'sorcery_points',
      changes.sorceryPointsDelta
    );
  }

  // Magic-item charges consumed — find each item by id in equipment and
  // decrement its `charges` field. Items whose charges hit 0 stay in equipment
  // (the engine emits a separate `item_removed` event when the item is
  // actually destroyed).
  //
  // We mirror the same delta into `permanent.magic_item_mechanics[].charges`
  // because that's what `CharDetailsPanel`'s actions list actually reads
  // (`permanent?.magic_item_mechanics`); without this, charge counts in the
  // action grid stay stale until the next full character snapshot lands.
  if (changes.itemChargesUsed && Object.keys(changes.itemChargesUsed).length > 0) {
    const used = changes.itemChargesUsed;
    // Read from updated.equipment so prior items_acquired/items_removed in
    // this same delta aren't clobbered.
    const equipment = (updated.equipment ?? char.equipment ?? []).map((entry) => {
      const consumed = used[entry.item_id ?? ''];
      if (typeof consumed !== 'number' || consumed <= 0) return entry;
      const current = typeof entry.charges === 'number' ? entry.charges : 0;
      return { ...entry, charges: Math.max(0, current - consumed) };
    });
    updated.equipment = equipment;

    if (char.permanent?.magic_item_mechanics) {
      const mechanics = char.permanent.magic_item_mechanics.map((entry) => {
        // MagicItemData TS interface uses `[key: string]: unknown`; item_id
        // isn't declared but is always present on the wire.
        const itemId = entry.item_id as string | undefined;
        const consumed = itemId ? used[itemId] : undefined;
        if (typeof consumed !== 'number' || consumed <= 0) return entry;
        const current = typeof entry.charges === 'number' ? entry.charges : 0;
        return { ...entry, charges: Math.max(0, current - consumed) };
      });
      updated.permanent = { ...char.permanent, magic_item_mechanics: mechanics };
    }
  }

  return updated;
}

/**
 * Replace a player character's data with authoritative full permanent + session blobs.
 * Syncs top-level GameCharacterInfo fields that UI panels read directly.
 */
function syncPlayerCharData(
  char: GameCharacterInfo,
  permanent: CharacterPermanent,
  session: CharacterSession,
  /**
   * Whether the observer is allowed to see this character's concentration
   * spell NAME. The friendly gate matches the server-side
   * `is_concentration_visible` predicate (`backend/.../visibility.rs`):
   * party-aligned observers see the spell, hostile observers see only
   * the boolean "is concentrating" flag. Defaults to `true` for the
   * own-character / party-member path. The full-roster game_state
   * snapshot loop in the `game_state` handler passes `false` for hostile
   * scene characters so their spell names don't leak.
   */
  friendlyToObserver: boolean = true
): GameCharacterInfo {
  const updated = { ...char };
  updated.permanent = permanent;
  updated.session = session;
  // Sync top-level fields that panels / computeGameVitals read
  updated.ac = permanent.ac;
  updated.abilities = permanent.abilities;
  updated.speed = typeof permanent.speed === 'number' ? permanent.speed : permanent.speed?.walk;
  updated.senses = permanent.senses;
  updated.spellcasting = permanent.spellcasting;
  updated.level = permanent.level;
  // Effective max HP = permanent + hp_max_bonus - hp_max_reduction. Matches
  // backend `CharacterData::max_hp()`. Without this, Aid's +5 HP max bump
  // surfaced as "45 / 40" (current > max) until a full reconnect.
  const hpMaxBonus = session.hp_max_bonus ?? 0;
  const hpMaxReduction = session.hp_max_reduction ?? 0;
  updated.maxHp = Math.max(0, permanent.max_hp + hpMaxBonus - hpMaxReduction);
  updated.proficiencyBonus = permanent.proficiency_bonus;
  updated.hitDie = permanent.hit_die;
  updated.hp = session.hp;
  updated.xp = permanent.xp; // permanent.xp is SOT for total accumulated XP
  updated.tempHp = session.temp_hp;
  // Spell name is gated by the friendly predicate (matches server's
  // `is_concentration_visible` in `round_processing/visibility.rs`).
  // Hostile observers see `isConcentrating: true` but no spell name —
  // the ConcentrationPill renders as "Concentrating" without the spell.
  updated.isConcentrating = session.concentration != null;
  updated.concentrationSpell = friendlyToObserver ? (session.concentration?.spell ?? null) : null;
  updated.conditions = session.conditions ?? [];
  updated.exhaustionLevel = session.exhaustion_level;
  updated.resurrectionPenalty = session.resurrection_penalty;
  updated.cover = session.cover;
  updated.deathSaveSuccesses = session.death_saves?.successes;
  updated.deathSaveFailures = session.death_saves?.failures;
  // Party-panel combat badges. Always assign (never guard on truthiness): when
  // Rust resets these to None/false, serde drops the key from the wire JSON, and
  // a falsy guard would leave the previous round's marker on screen.
  updated.heroicInspiration = session.heroic_inspiration ?? false;
  updated.lastSavingThrow = session.last_saving_throw;
  updated.critDealtThisRound = session.crit_dealt_this_round ?? false;
  updated.critTakenThisRound = session.crit_taken_this_round ?? false;
  // NOTE: deliberately does NOT touch `position`. `CharacterSession.combat_position`
  // is `#[serde(skip)]` on the Rust side, so it is never on the wire and reading
  // it here always produced `undefined`. Because this function runs LAST for the
  // player's own character, assigning from it also wiped the position that
  // `GameCharacterInfo.position` had correctly delivered, so the player was the
  // one character who could never get a token on the tactical map.
  // Their position arrives via the top-level `position` field instead, which does
  // serialize; observable views exclude the recipient, so that is the only source.
  // Backend-authoritative dead flag for the player's own char. permanent.status
  // is the durable death marker set by apply_state_changes() on every death
  // path; trusting it keeps the party tile DEAD (not UNCONSCIOUS) even when the
  // 'dead' condition isn't present in this session blob.
  updated.isDead = permanent.status === 'dead';
  updated.featureUses = session.feature_uses;
  // Always assign — when Rust resets to None (e.g. long rest), serde drops the
  // field from the wire JSON and a falsy guard would leave stale slots-used in
  // place. Empty object means "no slots spent".
  const mapped: Record<number, number> = {};
  for (const [level, count] of Object.entries(session.spell_slots_used ?? {})) {
    if (count) mapped[Number(level)] = count;
  }
  updated.spellSlotsUsed = mapped;
  // Extract prepared spells with level from session data.
  // Level uses D&D convention: 0 = cantrip, 1-9 = leveled spell.
  // Backend sends SpellData objects (always has base_level); string fallback is
  // defensive only — real data always has base_level set correctly.
  const rawPrepared = session.prepared_spells ?? [];
  const sessionPrepared = rawPrepared
    .map((s) => {
      if (typeof s === 'string') return { name: s, level: 0 };
      const obj = s as { name?: string; base_level?: number };
      // base_level: 0 = cantrip (valid), undefined = missing (fallback to 0)
      return { name: obj.name ?? '', level: obj.base_level ?? 0 };
    })
    .filter((s) => s.name);
  // Merge `always_prepared` (domain/subclass/feat grants — Magic Initiate
  // Cure Wounds lives here) into the prepared list so those spells stay
  // castable for the player regardless of the session's daily prep. Per SRD
  // 5.2.1 these don't count against the prepared cap. Known casters
  // (Bard/Sorcerer/Warlock) read castable spells from spells_known_by_level
  // and don't carry an always_prepared list, so the merge is a no-op for
  // them — keep the prepared list as the session value (typically empty).
  //
  // Dedupe is case-insensitive: Rangers carry Hunter's Mark in BOTH
  // session.prepared_spells AND spellcasting.always_prepared, and we must
  // not surface it twice.
  const isPreparedCaster = PREPARED_CASTER_CLASSES.has(
    (permanent.class ?? '').toLowerCase() as ClassName
  );
  const alwaysPreparedEntries: { name: string; level: number }[] = (
    (permanent.spellcasting?.always_prepared as Array<
      string | { name: string; base_level?: number }
    >) ?? []
  )
    .map((s) => {
      if (typeof s === 'string') return { name: s, level: 0 };
      return { name: s.name ?? '', level: (s as { base_level?: number }).base_level ?? 0 };
    })
    .filter((s) => s.name);

  // Always assign — same wire-omission concern as spellSlotsUsed above.
  const seenPrepared = new Set<string>();
  const merged: { name: string; level: number }[] = [];
  for (const entry of sessionPrepared) {
    const key = entry.name.toLowerCase();
    if (seenPrepared.has(key)) continue;
    seenPrepared.add(key);
    merged.push(entry);
  }
  // always_prepared is folded in for prepared casters (so domain/feat spells
  // stay castable when session.prepared_spells is empty mid-rest, or when
  // they're never daily-prepped at all). Known casters skip this merge —
  // their always_prepared list is conceptually empty per SRD.
  if (isPreparedCaster) {
    for (const entry of alwaysPreparedEntries) {
      const key = entry.name.toLowerCase();
      if (seenPrepared.has(key)) continue;
      seenPrepared.add(key);
      merged.push(entry);
    }
  }
  updated.preparedSpells = merged;
  if (updated.hp !== undefined && updated.maxHp !== undefined) {
    updated.healthState = deriveHealthState(
      updated.hp,
      updated.maxHp,
      updated.conditions,
      updated.isDead
    );
  }
  return updated;
}

// =============================================================================
// Types
// =============================================================================

export interface GameUIMessage {
  id: string;
  senderId?: string;
  senderName: string;
  avatarUrl?: string;
  avatarEmoji?: string;
  /** Full portrait URL for the sender — makes the sender avatar clickable to enlarge */
  senderPortraitUrl?: string;
  content: string;
  timestamp: string | Date;
  type: ChatMessageType;
  isStreaming?: boolean;
  /** Per-sender bubble color (hex) for visual distinction */
  bubbleColor?: string;
  /** True while this message is being progressively revealed (fake-streaming) */
  isQueuedReveal?: boolean;
  /**
   * Kind-specific metadata. For `type === 'token-gate'`: `action_kind`
   * (e.g. `'buyTokens'`) + `action_label`. Mirrors the lobby
   * `ChatMessage.meta` so the gate-bubble shape is identical across the
   * two stores.
   */
  meta?: Record<string, unknown>;
}

/**
 * Hard cap on retained chat messages. Long sessions (idle-round churn, 500+
 * rounds) otherwise grow `messages` without bound: every round appends several
 * bubbles, plus any orphaned `dm-narrative-stream-*` bubbles whose `done` frame
 * or preview cleanup never landed, until the renderer OOMs (BUG-6). Virtuoso
 * virtualizes the DOM, but the backing array + React reconciliation still scale
 * with length. Keep the newest N; scroll-back history and the persisted store
 * are bounded to the same window.
 */
const MAX_RENDERED_MESSAGES = 500;

/** Trim a message list to the newest MAX_RENDERED_MESSAGES, preserving order. */
function capMessages(messages: GameUIMessage[]): GameUIMessage[] {
  return messages.length > MAX_RENDERED_MESSAGES
    ? messages.slice(messages.length - MAX_RENDERED_MESSAGES)
    : messages;
}

/** Stable-id prefix for the single live DM narrative-stream bubble. */
const STREAM_BUBBLE_PREFIX = 'dm-narrative-stream-';

/**
 * Drop every `dm-narrative-stream-*` bubble. Only ONE DM stream is ever live,
 * so any survivor is stale: a prior-round orphan whose `done`/preview cleanup
 * never landed (interrupted/superseded rounds during idle-round churn, or a
 * scene transition arriving mid-stream), or a same-id bubble a wrong-round
 * preview left behind. Sweeping all keeps ids unique and caps unbounded orphan
 * growth — and, critically, clears any bubble stuck at `isStreaming: true`,
 * which otherwise pins `hasLiveStreaming` in the chat view so its auto-scroll
 * interval never stops (BUG-6).
 */
function withoutStreamBubbles(messages: GameUIMessage[]): GameUIMessage[] {
  return messages.filter((m) => !m.id.startsWith(STREAM_BUBBLE_PREFIX));
}

/**
 * Stop the typewriter cursor on EVERY live `dm-narrative-stream-*` bubble
 * without removing its text. Used at non-scene-change anchors (round_result,
 * game_state restore) where the prior narration should stay visible but no
 * bubble must linger at `isStreaming: true`. A leftover streaming orphan (a
 * label-mismatched round whose `done`/preview cleanup never landed) otherwise
 * keeps `hasLiveStreaming` true in the chat view, pinning its 100ms auto-scroll
 * interval — which, beside a looping inline combat/scene video, hangs the
 * renderer (BUG-6). Removal (`withoutStreamBubbles`) is for scene CHANGES where
 * the prior scene's partial text should also disappear.
 */
function finalizeStreamBubbles(messages: GameUIMessage[]): GameUIMessage[] {
  return messages.map((m) =>
    m.id.startsWith(STREAM_BUBBLE_PREFIX) && m.isStreaming ? { ...m, isStreaming: false } : m
  );
}

// ============================================================================
// REJECTED-INTENT RENDERING
// ============================================================================
// Rule: never leak technical text. Compose plain English from structured
// fields; otherwise show the softener. The backend mirrors this contract —
// `dev_reason` is server-side log fodder, never rendered. See
// `backend/shared/src/char_state/component.rs::RejectedIntent` for shape.

const SOFTENER_FALLBACK = "*That didn't quite work. 😕*";

/**
 * Stable ID for Master Kira's "reviewing your performance..." placeholder
 * shown during the duel-end LLM call. Replaced when `session_ended` arrives
 * with the actual recap + coaching.
 *
 * Exported so the ChatPanel `/end-session` intercept can detect "we're in the
 * LLM-gen window" by checking if this bubble is in `messages`. This is a
 * tighter signal than the duel-portraits localStorage flag, which can be
 * stale across sessions and would mis-trigger the client-side bail-out path
 * inside a regular adventure.
 */
export const DUEL_COACH_PLACEHOLDER_ID = 'duel-coach-placeholder';

/** Marker the backend uses to split the duel recap from Kira's coaching. */
const DUEL_COACHING_MARKER = "## Master Kira's Notes";

/**
 * Verb table for `intent_kind` → English verb. Must stay in sync with
 * `intent_kind_str()` in `backend/shared/src/char_state/component.rs`.
 * Unmapped kinds → softener.
 */
const REJECTION_VERBS: Record<string, string> = {
  Attack: 'attack',
  CastSpell: 'cast',
  UseAbility: 'use',
  UseItem: 'use',
  Interact: 'interact with',
  Open: 'open',
  Close: 'close',
  PickUp: 'pick up',
  Drop: 'drop',
  Equip: 'equip',
  Unequip: 'unequip',
  Give: 'give',
  GiveCoins: 'give',
  Buy: 'buy',
  Sell: 'sell',
  Travel: 'travel to',
  Move: 'move to',
  Hide: 'hide',
  Help: 'help',
  Search: 'search',
  Study: 'study',
  Observe: 'observe',
  Investigate: 'investigate',
  RequestCheck: 'attempt',
  ProposeDecision: 'propose',
  Vote: 'vote on',
  Stabilize: 'stabilize',
  WakeUp: 'wake',
  EscapeGrapple: 'escape',
  Say: 'say',
  TalkTo: 'talk to',
  Whisper: 'whisper to',
  Shout: 'shout',
  Emote: 'emote',
  Dash: 'dash',
  Wait: 'wait',
  Dodge: 'dodge',
  Disengage: 'disengage',
  SteadyAim: 'steady your aim',
  DeathSave: 'roll a death save',
  Flee: 'flee',
  DropProne: 'drop prone',
  StandUp: 'stand up',
  EndConcentration: 'end concentration',
  CancelProposal: 'cancel the proposal',
};

/**
 * Verbs that don't take an object/subject — render bare ("Failed to dodge.")
 * even with no structured target. Other verbs that show up here without an
 * object fall through to the playerReason-only path so the player still
 * sees actionable text instead of the softener.
 */
const SOLO_VERBS = new Set([
  'Wait',
  'Dodge',
  'Disengage',
  'SteadyAim',
  'DeathSave',
  'Flee',
  'DropProne',
  'StandUp',
  'EndConcentration',
  'CancelProposal',
]);

/** Capitalize first character without disturbing the rest of the string. */
function capitalizeSentence(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Compose the chat-bubble copy for a rejected intent. Structured fields only —
 * we never reach into `devReason` for player-visible text; it may contain
 * enum syntax, internal IDs, or dev-only routing hints.
 */
function composeRejectionMessage(ri: RejectedIntent): string {
  const kind = ri.intentKind ?? undefined;
  const verb = kind ? REJECTION_VERBS[kind] : undefined;
  const subject = ri.subjectName?.trim() || undefined;
  const object = ri.objectName?.trim() || undefined;
  const reason = ri.playerReason?.trim() || undefined;

  // Structured "Failed to {verb} {target}" when we have both a verb AND
  // something to attach. Highest-fidelity output.
  if (verb && (object || subject)) {
    let line: string;
    if (object && subject) {
      line = `Failed to ${verb} ${object} on ${subject}`;
    } else if (object) {
      line = `Failed to ${verb} ${object}`;
    } else {
      line = `Failed to ${verb} ${subject}`;
    }
    return reason ? `${line} — ${reason}.` : `${line}.`;
  }

  // Solo verb (no object expected): "Failed to dodge — you're already dodging."
  if (verb && kind && SOLO_VERBS.has(kind)) {
    return reason ? `Failed to ${verb} — ${reason}.` : `Failed to ${verb}.`;
  }

  // No verb mapping or verb expects an object that wasn't supplied — but we
  // still have a player-safe reason (the whole point of the field). Render
  // it directly as a sentence so non-leader/armor-in-combat/puzzle-spoiler
  // and similar rejections actually surface their copy instead of softening.
  if (reason) {
    return `${capitalizeSentence(reason)}.`;
  }

  return SOFTENER_FALLBACK;
}

/**
 * Build a Game Master message with an inline scene image marker.
 * Used for scene transitions, initial game start, and combat encounter images.
 * Callers pass a stable id (e.g. `combat-encounter-${round}`) so duplicate
 * dispatches (replay / reconnect drain) dedup via `enqueueMessages`. Embedding
 * `Date.now()` in the id was the previous pattern and silently leaked
 * duplicate media bubbles past the id-based dedup.
 */
function buildSceneImageMessage(
  scene: GameSceneInfo,
  id: string,
  opts: { preferEncounter?: boolean; includeDescription?: boolean } = {}
): GameUIMessage {
  const { preferEncounter = false, includeDescription = true } = opts;
  // Prefer video over image — SCENE_IMAGE marker auto-renders .mp4 as video
  const sceneMediaUrl = preferEncounter
    ? scene.encounterVideoUrl || scene.encounterImageUrl || scene.videoUrl || scene.imageUrl || null
    : scene.videoUrl || scene.imageUrl || null;

  let content = '';
  if (sceneMediaUrl) {
    content += `[SCENE_IMAGE:${sceneMediaUrl}]\n\n`;
  }
  content += `**${formatSceneName(scene.name)}**`;
  if (includeDescription && scene.description) {
    content += `\n\n${scene.description}`;
  }

  return {
    id,
    senderId: GAME_MASTER.charId,
    senderName: GAME_MASTER.name,
    avatarUrl: GAME_MASTER.avatarUrl,
    avatarEmoji: GAME_MASTER.avatarEmoji,
    content,
    timestamp: new Date().toISOString(),
    type: 'dm',
  };
}

/**
 * Whether a scene renders a LOOPING inline video (vs a static image). Mirrors
 * `buildSceneImageMessage`'s media selection: `videoUrl` always renders as
 * `<video>`, and an `imageUrl` ending in `.mp4` is auto-rendered as video by the
 * SCENE_IMAGE marker. Only a looping video (continuous decode/repaint) drives the
 * BUG-6 renderer hang beside the streaming auto-scroll; a static image, once
 * measured, does not.
 */
function sceneRendersLoopingVideo(scene: GameSceneInfo): boolean {
  return !!scene.videoUrl || (scene.imageUrl?.toLowerCase().endsWith('.mp4') ?? false);
}

export interface GameState {
  // Connection state machine
  connectionStatus: GameConnectionStatus;
  gameSessionId: string | null;
  /** True once the first round_result arrives (or on reconnect). Gates the game UI. */
  gameInitialized: boolean;

  // Session End Summary (set when session_ended message is received)
  sessionEndSummary: string | null;
  /**
   * True when the in-flight session-end is a training duel. Set from
   * `session_ending.isTrainingDuel` (and again on `session_ended`), read by
   * `game-connection-manager`'s 4100 close handler to decide whether to keep
   * the duel recap UI alive vs auto-nav to lobby. Decoupled from the
   * `game-session-duel-portraits` localStorage hint, which is for portrait
   * loading and gets cleared on unrelated paths.
   */
  sessionEndIsDuel: boolean;

  // Game State
  currentScene: GameSceneInfo | null;
  /** Stable scene id for current scene — used by the connection manager to
   * filter messages emitted on a prior scene during transitions (defense-in-depth
   * against scene-transition races where round results arrive after FE has
   * advanced to the next scene). Updated on game_state, scene_transition, and
   * movement_complete. */
  currentSceneId: string | null;
  characters: GameCharacterInfo[];
  currentRound: number;
  /**
   * Highest round whose `round_result` has been processed. Monotonic, advanced
   * ONLY by round_result (not by restore/catch-up, which set `currentRound`).
   * A `narrative_stream` chunk for a round <= this is post-result and therefore
   * redundant (the full narration already anchored in the preview/result bubble),
   * so it is dropped — the FE backstop for the BUG-6 late-chunk-after-round_result
   * race that the engine_actor stream-forwarder await fixes at the root.
   */
  lastResolvedRound: number;
  isSceneLoading: boolean;
  /** Current encounter type: exploration (default), social, or combat */
  encounterType: EncounterType;
  /** Last round's pipeline processing time in milliseconds */
  lastRoundProcessingMs: number | null;
  /** Current in-game time (structured data from backend) */
  inGameTime: InGameTimeData | null;

  // Player's character ID (for identifying which character belongs to this client)
  playerCharacterId: string | null;

  // Party leader's character ID (crown on the roster); null when there is no leader
  leaderCharacterId: string | null;

  // Module title for display
  moduleTitle: string | null;

  // Module hero image for background
  moduleHeroImage: string | null;

  // Party name for display
  partyName: string | null;

  // Party image URL (from game-server, updated on membership change)
  partyImageUrl: string | null;

  // Chat (Player & System)
  messages: GameUIMessage[];
  unreadCount: number;

  // Message reveal queue (fake-streaming)
  messageQueue: GameUIMessage[];
  revealingMessageId: string | null;

  // Party & Turn Info
  currentTurn: string | null; // Character ID whose turn it is
  partyMembers: GameCharacterInfo[];
  /** Non-party characters in scene (NPCs, monsters, animals) */
  sceneCharacters: GameCharacterInfo[];
  /** Map of charId -> GameCharacterInfo for O(1) lookups */
  partyMembersMap: Record<string, GameCharacterInfo>;

  // Scene Items & Interactables
  /** Items in the current scene (loot, quest items) */
  sceneItems: SceneItem[];
  /** Interactable objects in the current scene (doors, chests, levers) */
  sceneInteractables: SceneInteractable[];
  /** Reachable travel destinations for /travel command */
  travelDestinations: TravelDestination[];
  /**
   * Party-wide boons currently granted by quest flags. Full list on every
   * message that carries it, so it is assigned unconditionally: falling back
   * to the previous value would leave a pill up after its flag flipped off.
   */
  activeBoons: ActiveBoon[];

  // Early narrative streaming — tracks which round's preview is active
  // Used to clean up ghost preview messages on round failure
  streamingRound: number | null;

  // Real-time narrative token streaming (accumulated text from narrative_stream chunks)
  // Cleared when narrative_preview arrives (replaces with parsed content)
  streamingNarrative: string | null;
  streamingNarrativeRound: number | null;

  // BUG-6 scene-transition-mid-stream guard. Set true by a genuine
  // scene_transition into a looping-video scene; while set, live
  // `narrative_stream` chunks are dropped so a `dm-narrative-stream-*` bubble
  // can't be (re)created at `isStreaming: true` beside the just-mounted scene
  // video — which would pin `hasLiveStreaming` and hang the renderer. The
  // narration still renders via `narrative_preview`/`round_result`.
  // Lifts ONLY on a successful narration anchor (narrative_preview/round_result)
  // or a full state (re)load (game_state restore / reset). Deliberately NOT
  // lifted by round_failed/error: those replay the SAME round (the server
  // re-streams its narrative without re-broadcasting scene_transition), so the
  // retry's chunks must stay suppressed beside the still-mounted video — lifting
  // here would rebuild a live bubble and bring the freeze back. Ephemeral (not
  // in `partialize`).
  suppressStreamAfterSceneVideo: boolean;

  // True while waiting for round output (narrative_preview/round_result/error)
  // Set on: game_state R1, session-switch recap, player action submit
  // Cleared on: narrative_preview, round_result, round_failed, error, session_ended
  // Drives TypingIndicator in GameChatContent (same amber dots as lobby)
  isWaitingForRound: boolean;

  // Pre-tap snapshot of the player's character, captured by
  // `optimisticallyDecrementResource` and consumed by the standalone
  // `intent_rejected` handler — the one rejection path with no round, and so
  // no `my_character_session` snapshot to reconcile the counter. Cleared
  // whenever an authoritative payload lands. Not persisted (`partialize`
  // writes only `messages`), because it is meaningless across a reload.
  optimisticRestore: { charId: string; char: GameCharacterInfo } | null;

  // Warmup gate: the round number that was "current" when game_state arrived
  // on a session switch. Used to distinguish buffered-replay narrative events
  // (round <= sessionStartRound) from live post-gate streaming (round >
  // sessionStartRound). The thinking indicator should stay up across the
  // replay burst (recap + R1 content all drained together) and only clear
  // when a LIVE next-round chunk/preview/result arrives. Null once a live
  // event has cleared it, or when not in a session-switch context.
  sessionStartRound: number | null;

  // True between `warmup_holding` (the backend's definitive "warmup buffer is
  // active" signal, never sent on a resume) and the drained
  // `game_state`, which consumes it to arm `sessionStartRound` from
  // `currentRound` (the correct drain boundary in every release mode). A
  // resumed session never sets it, so its `game_state` disarms the gate and
  // the single live kickstart round clears the thinking indicator (#35).
  warmupActive: boolean;

  // Campaign Map State (for movement messages)
  currentLocationId: string | null;
  discoveredLocations: string[];
  discoveredScenes: string[];
  mapImageUrl: string | null;
  mapVideoUrl: string | null;

  /** Timer handle for duel recap delay — cleared on reset() to prevent stale redirects */
  _recapTimerId: ReturnType<typeof setTimeout> | null;

  // Campaign Journal
  campaignJournal: CampaignJournal | null;
  unreadJournalCount: number;

  // 3D combat world (WorldCanvas). Ephemeral, none of these persist.
  /** Latest render transcript awaiting playback; null once played or stale. */
  pendingPresentationRound: PresentationRound | null;
  /**
   * Watermark: highest presentation round_id already played. Dedupes
   * duplicate round_result deliveries (reconnect drains, dispatcher replays):
   * a round with round_id <= watermark is never stashed again.
   */
  lastPlayedPresentationRound: number;
  /**
   * Renderer-facing entity snapshot for board (re)build: final_entities of
   * the last stashed round, or derived from game_state characters on
   * (re)connect: the reconnect fast-forward target.
   */
  worldEntitySnapshot: EntityPresentationState[] | null;
  /**
   * Staged 3D movement rider (combat-3D plan Q13): the clicked destination
   * path, shown in the input area and sent as a `player_move` alongside the
   * next submitted action (or alone as a move-only turn). Cleared on send,
   * scene change, and round completion.
   */
  stagedMove: { path: { x: number; y: number; z: number }[]; distanceFt: number } | null;
  setStagedMove: (
    move: { path: { x: number; y: number; z: number }[]; distanceFt: number } | null
  ) => void;
  /**
   * In-world speech feed (Polymyth 2.0): chat lines + extracted dialogue
   * (`character_actions` of type 'speak') land as 3D bubbles. WorldCanvas
   * consumes by monotonic `seq`; bounded ring, ephemeral, never persisted.
   */
  worldSpeech: {
    seq: number;
    charId: string;
    text: string;
    emote?: EmoteGesture;
    /** Round that produced the line, so a failed round can take it back.
     * Absent for party chat, which belongs to no round. */
    round?: number;
    /** Wall clock at queue time. The consumer needs it to tell a line
     * belonging to the round about to play from a previous scene's
     * backlog, since both look the same by `seq` alone. */
    queuedAt: number;
  }[];
  queueWorldSpeech: (
    entries: {
      charId: string;
      text: string;
      emote?: EmoteGesture;
      dedupeId?: string;
      round?: number;
    }[]
  ) => void;
  /**
   * Last round whose world speech was retracted, with a nonce so two
   * failures of the SAME round number each fire the world-layer consumer.
   * WorldCanvas watches it to drop that round's parked lines.
   */
  retractedSpeechRound: { round: number; nonce: number } | null;
  /** Take back a round's world speech: parked lines, queued lines, and the
   * dedupe claim, so the retry's replacement line is not filtered as a dup. */
  retractWorldSpeechForRound: (round: number) => void;
  /** Q17 input gate: true while the 3D panel is playing a round for THIS
   * client; the action input stays disabled until playback completes. */
  presentationPlaybackActive: boolean;
  setPresentationPlaybackActive: (active: boolean) => void;

  // Actions
  setConnectionStatus: (status: GameConnectionStatus) => void;
  setGameSessionId: (id: string | null) => void;

  updateScene: (scene: GameSceneInfo) => void;
  setSceneLoading: (isLoading: boolean) => void;

  addMessage: (message: GameUIMessage) => void;
  /**
   * Append a client-only italicized chat bubble (no WS frame, no LLM
   * round-trip). Used by the slash-command / action-card layer to surface
   * "can't do that here" feedback in-chat instead of via a toast — e.g.
   * tapping /attack in a peaceful scene renders "*No enemies in sight.*"
   * as a system bubble. The bubble is attributed to the player character
   * when `charId` is known so it visually reads as a beat from the player.
   */
  addLocalBubble: (text: string, charId?: string | null) => void;
  clearMessages: () => void;
  removePreviewMessages: (round: number) => void;

  // Message queue actions (fake-streaming reveal)
  enqueueMessages: (messages: GameUIMessage[]) => void;
  advanceQueue: () => void;
  finishReveal: (id: string) => void;
  skipQueue: () => void;

  clearJournalUnread: () => void;

  /**
   * Optimistically decrement a character's resource on click — counter drops
   * immediately, snapshot reconciles when the round result arrives. Reads
   * `requirement.resource` and dispatches to the matching session field. The
   * authoritative `my_character_session` snapshot precedence in
   * `syncPlayerCharData` overwrites this on rejection (counter rolls back) or
   * confirms it on success (counter stays decremented).
   *
   * `cardLabel` keys into `featureUses` for `feature_use` resources.
   */
  optimisticallyDecrementResource: (
    charId: string,
    requirement: ResourceRequirement,
    cardLabel?: string
  ) => void;

  /**
   * Snapshot a character before an optimistic mutation, so the standalone
   * `intent_rejected` handler can put it back. `optimisticallyDecrementResource`
   * does this itself; call it directly for optimistic paths that bypass the
   * resource counters, like magic-item charges. First capture since the last
   * authoritative sync wins, so a restore undoes the whole run.
   */
  captureOptimisticRestore: (charId: string) => void;

  /**
   * WorldCanvas playback completion: advances the presentation watermark and
   * clears the pending round it covers.
   */
  markPresentationRoundPlayed: (roundId: number) => void;

  handleGameEvent: (event: ServerMessage) => void;
  reset: () => void;
}

const initialState = {
  connectionStatus: { status: 'idle' } as GameConnectionStatus,
  gameSessionId: null,
  gameInitialized: false,
  sessionEndSummary: null,
  sessionEndIsDuel: false,
  currentScene: null,
  currentSceneId: null,
  characters: [],
  currentRound: 0,
  lastResolvedRound: -1,
  isSceneLoading: false,
  encounterType: 'exploration' as EncounterType,
  lastRoundProcessingMs: null,
  inGameTime: null,
  playerCharacterId: null,
  leaderCharacterId: null,
  moduleTitle: null,
  moduleHeroImage: null,
  partyName: null,
  partyImageUrl: null,
  messages: [],
  unreadCount: 0,
  messageQueue: [],
  revealingMessageId: null,
  currentTurn: null,
  partyMembers: [],
  sceneCharacters: [],
  partyMembersMap: {} as Record<string, GameCharacterInfo>,
  streamingRound: null,
  streamingNarrative: null,
  streamingNarrativeRound: null,
  suppressStreamAfterSceneVideo: false,
  isWaitingForRound: false,
  optimisticRestore: null,
  sessionStartRound: null,
  warmupActive: false,
  // Scene Items & Interactables
  sceneItems: [] as SceneItem[],
  sceneInteractables: [] as SceneInteractable[],
  travelDestinations: [] as TravelDestination[],
  activeBoons: [] as ActiveBoon[],
  // Campaign Map State
  currentLocationId: null,
  discoveredLocations: [],
  discoveredScenes: [],
  mapImageUrl: null,
  mapVideoUrl: null,
  _recapTimerId: null as ReturnType<typeof setTimeout> | null,
  // Campaign Journal
  campaignJournal: null as CampaignJournal | null,
  unreadJournalCount: 0,
  // 3D combat world
  pendingPresentationRound: null as PresentationRound | null,
  lastPlayedPresentationRound: 0,
  worldEntitySnapshot: null as EntityPresentationState[] | null,
  stagedMove: null as {
    path: { x: number; y: number; z: number }[];
    distanceFt: number;
  } | null,
  presentationPlaybackActive: false,
  worldSpeech: [] as {
    seq: number;
    charId: string;
    text: string;
    emote?: EmoteGesture;
    round?: number;
    queuedAt: number;
  }[],
  retractedSpeechRound: null as { round: number; nonce: number } | null,
};

// In-world speech ordering + dedup (module scope, ephemeral like the
// queue itself): `seq` keys consumption, the id ring dedups the preview
// and round_result deliveries of the same spoken lines.
let worldSpeechSeq = 0;
const worldSpeechSeenIds = new Set<string>();
const worldSpeechSeenOrder: string[] = [];

/**
 * Release a failed round's dedupe claims.
 *
 * The server rebuilds action ids as `action-{round}-{char}-{i}` and a retry
 * reuses the round number, so without this the retry's differently-worded
 * line is dropped as a duplicate of the attempt the engine threw away, and
 * the discarded wording is the only copy the world ever holds.
 */
function purgeWorldSpeechDedupeForRound(round: number): void {
  const prefix = `action-${round}-`;
  for (let i = worldSpeechSeenOrder.length - 1; i >= 0; i--) {
    const id = worldSpeechSeenOrder[i];
    if (!id.startsWith(prefix)) continue;
    worldSpeechSeenIds.delete(id);
    worldSpeechSeenOrder.splice(i, 1);
  }
}

/**
 * The spoken line inside a character action, or null when the action
 * carries no speech. Two wire shapes exist: engine Say/TalkTo intents
 * arrive as action_type 'speak' with `Says[ to X]: "..."` descriptions,
 * and roleplay narrative entries arrive as action_type 'action' with
 * dialogue embedded in prose. Bubbles carry ONLY the quoted speech,
 * never the narration around it.
 */
export function extractSpokenLine(action: {
  action_type: string;
  description: string;
}): string | null {
  if (action.action_type === 'speak') {
    const quoted = action.description.match(/"([\s\S]+)"\s*$/);
    const line = (quoted?.[1] ?? action.description).trim();
    return line.length > 0 ? line : null;
  }
  if (action.action_type === 'action') {
    const quoted = action.description.match(/"([^"]{2,})"/);
    const line = quoted?.[1]?.trim() ?? '';
    return line.length > 0 ? line : null;
  }
  return null;
}

/** Emote gestures the hero bodies ship as one-shot clips (emote_<id>). */
export type EmoteGesture = 'wave' | 'clap' | 'yes' | 'no' | 'shrug';

/**
 * The physical gesture in a non-speech character action, or null.
 * Leading-verb match ONLY ("Mira waves to the party", "Waves them over"):
 * a gesture verb mid-sentence ("waves off the idea") never fires, so the
 * false-positive cost of prose stays near zero. Speech wins over
 * gestures; callers check extractSpokenLine first.
 */
export function extractEmoteGesture(action: {
  action_type: string;
  description: string;
}): EmoteGesture | null {
  if (action.action_type !== 'action' && action.action_type !== 'emote') return null;
  // Up to three leading capitalized tokens cover actor names ("One Man
  // Gang waves"); lowercase prose before the verb disqualifies. The verb
  // spells out its own capitals instead of /i: a case-insensitive [A-Z]
  // would let ANY three words lead and break the mid-sentence guard.
  const m = action.description
    .trim()
    .match(
      /^(?:[A-Z][\w'-]* ){0,3}([Ww]aves?|[Cc]laps?|[Aa]pplauds?|[Nn]ods?|[Ss]hrugs?|[Ss]hakes?)\b(.*)$/
    );
  if (!m) return null;
  const verb = m[1].toLowerCase();
  if (verb.startsWith('wave')) return 'wave';
  if (verb.startsWith('clap') || verb.startsWith('applaud')) return 'clap';
  if (verb.startsWith('nod')) return 'yes';
  if (verb.startsWith('shrug')) return 'shrug';
  // Only a head shake is an answer; shaking anything else is not.
  if (/^\s+(?:his|her|their|its)\s+head\b/i.test(m[2] ?? '')) return 'no';
  return null;
}

// Fail-soft persistence: zustand `persist` writes storage on EVERY `set(...)`.
// A throwing write (QuotaExceeded when localStorage is full, SecurityError in
// some privacy modes) would propagate out of the `set()` and abort the caller
// mid-transition. That was the root of #349: a storage throw inside the
// `session_ended` handler stopped it from reaching `status: 'ended'`, freezing
// the dead-session UI. Swallow write failures so a full disk degrades to
// "no persistence" instead of breaking critical state transitions.
const failSoftStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch {
      // Some privacy modes throw on access rather than returning null. Treat as
      // "no persisted state" so rehydration at store creation can't crash.
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      console.warn('[gameStore] persist write failed (storage full?), continuing unpersisted', e);
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

export const useGameStore = create<GameState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...initialState,

        setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
        setGameSessionId: (gameSessionId) => set({ gameSessionId }),
        setStagedMove: (stagedMove) => set({ stagedMove }),
        setPresentationPlaybackActive: (presentationPlaybackActive) =>
          set({ presentationPlaybackActive }),
        queueWorldSpeech: (entries) => {
          const fresh = entries.filter((e) => {
            if (!e.charId || (e.text.trim().length === 0 && !e.emote)) return false;
            if (!e.dedupeId) return true;
            if (worldSpeechSeenIds.has(e.dedupeId)) return false;
            worldSpeechSeenIds.add(e.dedupeId);
            worldSpeechSeenOrder.push(e.dedupeId);
            if (worldSpeechSeenOrder.length > 300) {
              worldSpeechSeenIds.delete(worldSpeechSeenOrder.shift()!);
            }
            return true;
          });
          if (fresh.length === 0) return;
          const queuedAt = Date.now();
          set((s) => ({
            worldSpeech: [
              ...s.worldSpeech,
              ...fresh.map((e) => ({
                seq: ++worldSpeechSeq,
                charId: e.charId,
                text: e.text,
                emote: e.emote,
                round: e.round,
                queuedAt,
              })),
            ].slice(-50),
          }));
        },

        retractWorldSpeechForRound: (round) => {
          purgeWorldSpeechDedupeForRound(round);
          set((prev) => ({
            worldSpeech: prev.worldSpeech.filter((e) => e.round !== round),
            retractedSpeechRound: {
              round,
              nonce: (prev.retractedSpeechRound?.nonce ?? 0) + 1,
            },
          }));
        },

        updateScene: (scene) =>
          set({
            currentScene: scene,
            isSceneLoading: false,
          }),

        setSceneLoading: (isSceneLoading) => set({ isSceneLoading }),

        addMessage: (message) =>
          set((state) => {
            // Skip messages with empty/whitespace-only content — prevents empty chat bubbles
            // (can happen when backend sends empty narratives or whitespace-only text)
            if (!message.content?.trim() && !message.isStreaming) return state;

            // Queued round bubbles are OLDER than this message. With the
            // game typewriter the queue drains over seconds, so a player
            // echo / chat broadcast / error arriving mid-drain would
            // otherwise render ABOVE bubbles that later pop out of the
            // queue below it. Dump the queue (finalized, no animation)
            // before appending; the mid-reveal bubble already sits in
            // `messages` and finishes on its own.
            let baseMessages = state.messages;
            let flushedCount = 0;
            if (state.messageQueue.length > 0) {
              const dumped = state.messageQueue.map((m) => ({
                ...m,
                isStreaming: false,
                isQueuedReveal: false,
              }));
              baseMessages = [...baseMessages, ...dumped];
              flushedCount = dumped.length;
            }

            // Insert message in timestamp order (optimized for near-sorted data)
            const newTimestamp = new Date(message.timestamp).getTime();
            const messages = baseMessages;

            // Fast path: if message is newer than last, just append
            if (
              messages.length === 0 ||
              newTimestamp >= new Date(messages[messages.length - 1].timestamp).getTime()
            ) {
              return {
                messages: capMessages([...messages, message]),
                messageQueue: flushedCount > 0 ? [] : state.messageQueue,
                unreadCount: state.unreadCount + 1 + flushedCount,
              };
            }

            // Slow path: binary search for insert position
            let low = 0;
            let high = messages.length;
            while (low < high) {
              const mid = (low + high) >>> 1;
              if (new Date(messages[mid].timestamp).getTime() < newTimestamp) {
                low = mid + 1;
              } else {
                high = mid;
              }
            }
            const result = [...messages.slice(0, low), message, ...messages.slice(low)];
            return {
              messages: capMessages(result),
              messageQueue: flushedCount > 0 ? [] : state.messageQueue,
              unreadCount: state.unreadCount + 1 + flushedCount,
            };
          }),

        addLocalBubble: (text, charId) => {
          const trimmed = text.trim();
          if (!trimmed) return;
          // Wrap italics + a unique id so React doesn't dedupe back-to-back
          // bubbles. Renders via the 'system' type — same path as the
          // SOFTENER_FALLBACK narration beat so the styling is consistent.
          const message: GameUIMessage = {
            id: `local-bubble-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            senderId: charId ?? undefined,
            senderName: '',
            content: trimmed.startsWith('*') ? trimmed : `*${trimmed}*`,
            timestamp: new Date().toISOString(),
            type: 'system',
          };
          get().addMessage(message);
        },

        clearMessages: () =>
          set({ messages: [], unreadCount: 0, messageQueue: [], revealingMessageId: null }),

        removePreviewMessages: (round) =>
          set((prev) => {
            // DM and private-narrative preview ids are 1-per-round (no suffix) →
            // exact match. Whisper preview is per-sender → startsWith with
            // trailing dash. Action preview is per-character-per-idx → same.
            const isPreview = (id: string) =>
              id.startsWith(`action-${round}-`) ||
              id === `dm-narrative-preview-${round}` ||
              id === `private-narrative-preview-${round}` ||
              id.startsWith(`whisper-preview-${round}-`) ||
              id === `dm-narrative-stream-${round}`;
            // Clear revealingMessageId if the actively-revealing message is a preview
            // being removed — prevents the queue driver from waiting forever.
            const revealingStale =
              prev.revealingMessageId !== null && isPreview(prev.revealingMessageId);
            return {
              streamingRound: null,
              streamingNarrative: null,
              streamingNarrativeRound: null,
              revealingMessageId: revealingStale ? null : prev.revealingMessageId,
              messages: prev.messages.filter((msg) => !isPreview(msg.id)),
              messageQueue: prev.messageQueue.filter((msg) => !isPreview(msg.id)),
            };
          }),

        // Message queue actions (fake-streaming reveal)
        enqueueMessages: (msgs) =>
          set((state) => {
            // Dedup by stable id against both messages and the queue. Action
            // bubbles use server-generated ids (action-{round}-{char}-{i})
            // that are shared between narrative_preview and round_result —
            // the round_result handler dedups, but the preview handler did
            // not, leaving a gap where the same emote/talk_to bubble could
            // land twice if a preview event was processed more than once
            // (replay, reconnect drain, dispatcher race). Centralising the
            // dedup here protects every caller. DM/whisper/private bubbles
            // use per-event timestamped ids so they're unaffected.
            const existing = new Set<string>();
            for (const m of state.messages) existing.add(m.id);
            for (const m of state.messageQueue) existing.add(m.id);
            const fresh: GameUIMessage[] = [];
            for (const m of msgs) {
              if (!m.content?.trim()) continue;
              if (existing.has(m.id)) continue;
              existing.add(m.id);
              fresh.push(m);
            }
            if (fresh.length === 0) return state;
            // Cap the queue too: while the game chat is unmounted (e.g. the
            // journal tab), `useMessageQueue` stops draining, so round events
            // keep enqueueing with nothing advancing them into `messages` —
            // an unbounded memory leak that feeds the same OOM (BUG-6). Keep
            // the newest; dropping the oldest un-revealed entries is consistent
            // with the `messages` cap (they'd be trimmed there anyway).
            return { messageQueue: capMessages([...state.messageQueue, ...fresh]) };
          }),

        advanceQueue: () =>
          set((state) => {
            if (state.messageQueue.length === 0) return state;
            const [next, ...rest] = state.messageQueue;
            // isQueuedReveal only, NOT isStreaming: queued round bubbles
            // carry their final content. With the game typewriter enabled,
            // ProgressiveReveal idles at the buffer tail while isStreaming
            // is true and never fires onComplete, but finishReveal is the
            // only thing that clears the flag, so each bubble would
            // deadlock until the stuck-reveal watchdog (~5.5s pause after
            // every bubble). Live streams keep their real isStreaming flag.
            const revealed: GameUIMessage = { ...next, isQueuedReveal: true };
            return {
              messageQueue: rest,
              revealingMessageId: next.id,
              messages: capMessages([...state.messages, revealed]),
              unreadCount: state.unreadCount + 1,
            };
          }),

        finishReveal: (id) =>
          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === id ? { ...m, isStreaming: false, isQueuedReveal: false } : m
            ),
            revealingMessageId: state.revealingMessageId === id ? null : state.revealingMessageId,
          })),

        skipQueue: () =>
          set((state) => {
            // Finish current revealing message + dump all remaining queued messages at once
            const finishedMessages = state.messages.map((m) =>
              m.id === state.revealingMessageId
                ? { ...m, isStreaming: false, isQueuedReveal: false }
                : m
            );
            const remaining = state.messageQueue.map((m) => ({
              ...m,
              isStreaming: false,
              isQueuedReveal: false,
            }));
            return {
              messages: capMessages([...finishedMessages, ...remaining]),
              messageQueue: [],
              revealingMessageId: null,
              unreadCount: state.unreadCount + remaining.length,
            };
          }),

        clearJournalUnread: () => set({ unreadJournalCount: 0 }),

        captureOptimisticRestore: (charId) => {
          set((state) => {
            if (state.optimisticRestore) return {};
            const pre = state.partyMembersMap[charId];
            return pre ? { optimisticRestore: { charId, char: pre } } : {};
          });
        },

        optimisticallyDecrementResource: (charId, requirement, cardLabel) => {
          const amount = requirement.amount > 0 ? requirement.amount : 1;
          // Negative delta = consumed; sorcery_points is the one resource that
          // uses signed remaining-semantics, so its branch flips the sign.
          set((state) => {
            // Restore point for a rejection that arrives WITHOUT a round
            // (`intent_rejected`), where no authoritative snapshot follows to
            // reconcile the counter. Only the first tap since the last
            // authoritative sync is captured, so restoring undoes the whole
            // run of optimistic decrements rather than just the last one.
            const restore =
              state.optimisticRestore ??
              (() => {
                const pre = state.partyMembersMap[charId];
                return pre ? { charId, char: pre } : null;
              })();
            const updateChar = (char: GameCharacterInfo): GameCharacterInfo => {
              if (char.charId !== charId) return char;
              const updated = { ...char };
              const res = requirement.resource;
              switch (res) {
                case 'feature_use': {
                  // Generic catch-all — any feat / ability / class feature with
                  // a 1/LR or PB/LR counter (Magic Initiate, species spell,
                  // Lucky, Indomitable substitutes, Bardic Inspiration outcome
                  // tracker, etc) lands in `feature_uses` keyed by the
                  // ability's display name. CardLabel must be provided.
                  if (!cardLabel) return char;
                  // Always 1, NOT `amount`. On a FeatureUse cost `amount` is the
                  // cap per long rest, and the backend increments the counter by
                  // exactly 1 per use. Adding `amount` optimistically spent the
                  // whole allowance on the first click, so a 2-use Innate
                  // Sorcery went straight to 2/2 and greyed out until the next
                  // round_result re-synced it from the session blob.
                  const featureUses = { ...(char.featureUses ?? {}) };
                  featureUses[cardLabel] = (featureUses[cardLabel] ?? 0) + 1;
                  updated.featureUses = featureUses;
                  // Also mirror into session.feature_uses so panels reading
                  // session-derived state see the same value (after a server
                  // round_result, syncPlayerCharData re-syncs from session).
                  if (updated.session) {
                    const sessionFu = { ...(updated.session.feature_uses ?? {}) };
                    sessionFu[cardLabel] = (sessionFu[cardLabel] ?? 0) + 1;
                    updated.session = { ...updated.session, feature_uses: sessionFu };
                  }
                  break;
                }
                case 'spell_slot': {
                  // Card carries `min_slot_level` — that's the slot the cast
                  // will consume (engine may upcast, but the floor is what
                  // we pre-decrement). Cantrips (level 0) have no counter.
                  const lvl = requirement.min_slot_level ?? 0;
                  if (lvl < 1 || lvl > 9) break;
                  const slotsUsed = { ...(char.spellSlotsUsed ?? {}) };
                  slotsUsed[lvl] = (slotsUsed[lvl] ?? 0) + amount;
                  updated.spellSlotsUsed = slotsUsed;
                  if (updated.session) {
                    const sessSlots = { ...(updated.session.spell_slots_used ?? {}) } as Record<
                      number,
                      number | undefined
                    >;
                    sessSlots[lvl] = (sessSlots[lvl] ?? 0) + amount;
                    updated.session = {
                      ...updated.session,
                      spell_slots_used: sessSlots as typeof updated.session.spell_slots_used,
                    };
                  }
                  break;
                }
                case 'arcane_recovery': {
                  // A boolean, not a counter: SRD 5.2.1 allows one use per Long
                  // Rest. Every other resource greys its card out on click and
                  // this one didn't, so a Wizard could keep clicking a spent
                  // recovery until the next authoritative snapshot landed.
                  if (updated.session) {
                    updated.session = {
                      ...updated.session,
                      class_resources: {
                        ...updated.session.class_resources,
                        arcane_recovery_available: false,
                      },
                    };
                  }
                  break;
                }
                case 'sorcery_points':
                case 'rage':
                case 'bardic_inspiration':
                case 'channel_divinity':
                case 'wild_shape':
                case 'action_surge':
                case 'second_wind':
                case 'indomitable':
                case 'focus_points':
                case 'ki':
                case 'lay_on_hands_pool':
                case 'pact_magic_slots': {
                  // Sorcery points uses signed remaining-semantics — pass a
                  // negative delta. All other resources are SPENT counters
                  // (positive delta increments the *_used field).
                  const delta = res === 'sorcery_points' ? -amount : amount;
                  // Same max resolution the Actions panel uses: published
                  // `max_sorcery_points`, else character level.
                  const maxSp =
                    res === 'sorcery_points'
                      ? (() => {
                          const abilities = char.permanent?.class_abilities as
                            | Record<string, unknown>
                            | undefined;
                          const raw = abilities?.max_sorcery_points;
                          const published =
                            typeof raw === 'number'
                              ? raw
                              : raw && typeof raw === 'object' && 'value' in raw
                                ? (raw as { value: unknown }).value
                                : undefined;
                          return typeof published === 'number' && published > 0
                            ? published
                            : (char.level ?? 0);
                        })()
                      : undefined;
                  updated.session = applyClassResourceDelta(updated.session, res, delta, maxSp);
                  break;
                }
                case 'charge': {
                  // Magic-item charges. The card click flows through
                  // `use_item` activation — we need the item id, not the
                  // amount. Skip here; the calling site is responsible for
                  // calling decrementItemCharges separately when it knows
                  // the item_id (see ChatPanel pre-send hook).
                  break;
                }
                // Resource categories not currently tracked in the typed
                // session (superiority_dice, eldritch_invocation, etc.) —
                // backed by feature_uses on the server. The card's label
                // would route through `feature_use` instead. Silently
                // ignore here so unmapped categories don't error.
                default:
                  break;
              }
              return updated;
            };

            // Apply across both partyMembers and sceneCharacters; mirror to map.
            const partyMembers = state.partyMembers.map(updateChar);
            const sceneCharacters = state.sceneCharacters.map(updateChar);
            const partyMembersMap = { ...state.partyMembersMap };
            if (partyMembersMap[charId]) {
              partyMembersMap[charId] = updateChar(partyMembersMap[charId]);
            }
            return { partyMembers, sceneCharacters, partyMembersMap, optimisticRestore: restore };
          });
        },

        handleGameEvent: (event) => {
          const state = get();

          // Audio messages are server-pushed soundtrack / FX directives.
          // Route to the singleton SoundtrackEngine before any
          // game-state logic — they don't touch store state.
          if (handleGameAudioMessage(event)) return;

          switch (event.type) {
            case 'game_state': {
              const previousTurn = state.currentTurn;
              // Build map for O(1) lookups (single source of truth for party member info)
              // Derive healthState from HP for initial render (no observable views yet)
              const partyMembersMap: Record<string, GameCharacterInfo> = {};
              for (const char of event.state.characters) {
                if (!char.healthState) {
                  char.healthState = deriveHealthState(
                    char.hp,
                    char.maxHp,
                    char.conditions,
                    char.isDead
                  );
                }
                // Sync derived fields from full permanent/session blobs.
                // Backend sends raw blobs but top-level fields like spellSlotsUsed,
                // abilities, preparedSpells etc. are only populated by syncPlayerCharData.
                // Without this, the vitals panel shows stale/empty data until first round_result.
                if (char.permanent && char.session) {
                  const perm =
                    typeof char.permanent === 'string'
                      ? JSON.parse(char.permanent)
                      : char.permanent;
                  const sess =
                    typeof char.session === 'string' ? JSON.parse(char.session) : char.session;
                  // Friendly gate: party-aligned characters (own char +
                  // party members) see each other's concentration spell
                  // name; hostile observers don't. Matches the server's
                  // `is_concentration_visible` predicate so a reconnecting
                  // player can't read an enemy mage's Hold Person off the
                  // initial game_state blob.
                  const friendly = char.isPartyMember === true;
                  const synced = syncPlayerCharData(char, perm, sess, friendly);
                  Object.assign(char, synced);
                }
                partyMembersMap[char.charId] = char;
              }
              // Separate party members from scene characters based on isPartyMember flag
              // Party panel: all party members (PCs and party NPCs)
              // Scene panel: all non-party characters (NPCs, monsters, animals not in party)
              const partyMembers = event.state.characters.filter((c) => c.isPartyMember);
              const sceneCharacters = event.state.characters.filter((c) => !c.isPartyMember);

              // Session-switch guard: clear stale persisted messages from a different session
              // Uses a separate localStorage key because gameSessionId is not persisted
              // (it's a UI-mode flag that must be null when in lobby)
              const lastSessionId = localStorage.getItem('game-last-session-id');
              const hasPersistedMessages = get().messages.length > 0;
              const isSessionSwitch =
                lastSessionId == null
                  ? hasPersistedMessages // fresh load with stale persist data from prior session
                  : lastSessionId !== event.state.sessionId;
              localStorage.setItem('game-last-session-id', event.state.sessionId);

              set({
                // Clear messages on session switch to prevent stale chat from previous session
                ...(isSessionSwitch ? { messages: [] } : {}),
                gameSessionId: event.state.sessionId,
                currentRound: event.state.currentRound,
                currentScene: event.state.scene,
                currentSceneId: event.state.scene?.sceneId ?? null,
                characters: event.state.characters,
                partyMembers, // Only PCs shown in party panel
                sceneCharacters, // NPCs, monsters, animals shown in scene chars panel
                partyMembersMap, // O(1) lookup map for all characters
                currentTurn: event.state.currentTurn || null,
                playerCharacterId: event.state.playerCharacterId || null,
                leaderCharacterId: event.state.leaderCharacterId ?? null,
                moduleTitle: event.state.moduleTitle || null,
                moduleHeroImage: event.state.moduleHeroImage || null,
                partyName: event.state.partyName || null,
                partyImageUrl: event.state.partyImageUrl || null,
                mapImageUrl: event.state.mapUrl ?? null,
                mapVideoUrl: event.state.mapVideoUrl ?? null,
                encounterType: event.state.encounterType ?? 'exploration',
                inGameTime: event.state.inGameTime ?? null,
                isSceneLoading: false,
                sceneItems: event.state.sceneItems ?? [],
                sceneInteractables: event.state.sceneInteractables ?? [],
                travelDestinations: event.state.travelDestinations ?? [],
                activeBoons: event.state.activeBoons ?? [],
                campaignJournal: event.state.campaignJournal ?? null,
                unreadJournalCount: 0,
                // 3D world: game_state is the authoritative rebuild point.
                // Any stashed transcript predates this snapshot (missed rounds
                // are narrative-only): drop it and fast-forward the board to
                // the characters' current positions. The watermark survives
                // same-session reconnects (so a re-delivered round_result
                // can't replay), resets on session switch, and during warmup
                // release advances to currentRound (the drain boundary; the
                // snapshot already reflects the buffered rounds, which drain
                // in AFTER it and must not rewind the fast-forwarded board).
                pendingPresentationRound: null,
                worldEntitySnapshot: worldSnapshotFromGameState(event.state.characters),
                lastPlayedPresentationRound: presentationWatermarkAfterGameState({
                  warmupActive: state.warmupActive,
                  isSessionSwitch,
                  currentRound: event.state.currentRound,
                  lastPlayedRound: state.lastPlayedPresentationRound,
                }),
                gameInitialized: true,
                // Authoritative snapshot — any pre-tap restore point is stale.
                optimisticRestore: null,
                // Show thinking dots for fresh R1 (hides when narrative_preview/round_result arrives).
                // Preserve `true` ONLY during warmup — `round_started` arrives from the backend before
                // `game_state` drains from the warmup buffer, and we must not flicker the indicator off
                // on that drain. Outside warmup the flag is NOT preserved: a steady-state reconnect that
                // raced past a round's completion must clear it (else it sticks forever under the
                // player-gated scheduler, since no live round follows). If a round is genuinely still
                // executing, the backend re-sends `round_started` right after this `game_state` (see
                // connection_management.rs::add_connection), re-arming the indicator in order.
                isWaitingForRound:
                  (state.warmupActive && state.isWaitingForRound) ||
                  (event.state.currentRound === 1 && (isSessionSwitch || !state.gameInitialized)),
                // Warmup-replay gate: arm from THIS message's currentRound (the
                // drain boundary, correct in every warmup release mode, e.g. 2
                // when the drain carries R1+R2), but ONLY when `warmup_holding`
                // flagged an active warmup buffer. A resumed session never gets
                // warmup_holding (a reconnect during a fresh session's warmup
                // does), so the gate disarms here and the single
                // live "kickstart" round clears the thinking indicator normally
                // instead of being misread as buffered replay (which left it
                // stuck on "thinking..." forever, #35).
                sessionStartRound: state.warmupActive ? event.state.currentRound : null,
                warmupActive: false,
              });

              // Reconnect/restore anchor: finalize any in-memory orphan stream
              // bubble (a live narration interrupted by the disconnect) so it
              // can't sit at `isStreaming: true` beside the scene-initial inline
              // video below and pin `hasLiveStreaming` → renderer hang (BUG-6).
              // Persisted messages already drop `isStreaming` in `partialize`, so
              // this only matters for same-session reconnects holding an
              // in-flight stream.
              set((s) => ({
                messages: finalizeStreamBubbles(s.messages),
                suppressStreamAfterSceneVideo: false,
              }));
              // Show scene image on connect (fresh start or reconnect)
              // Guard against duplicates from WS reconnect
              {
                const alreadyHasSceneImage = get().messages.some(
                  (m) => m.id.startsWith('scene-initial-') || m.id.startsWith('scene-transition-')
                );
                const initialScene = event.state.scene;
                if (initialScene?.imageUrl && !alreadyHasSceneImage) {
                  state.addMessage(
                    buildSceneImageMessage(initialScene, `scene-initial-${initialScene.sceneId}`, {
                      preferEncounter: false,
                    })
                  );
                }
              }

              // Previous session recap (show on every session start, not just round 1)
              // Prefer server-side recap (LLM-generated from campaign journal) over localStorage fallback
              // Guard against duplicates from WS reconnect
              {
                const alreadyHasRecap = get().messages.some((m) => m.id.startsWith('recap-'));
                // `game-session-last-summary` is a GLOBAL (not per-party) fallback
                // written whenever any real adventure session ends. A training-duel
                // session has no adventure history of its own: falling back to it
                // leaks whatever unrelated adventure last ended into the duel's
                // "Previously..." block. Mirrors the backend's own
                // `TRAINING_PARTY_PREFIX` convention (lobby-server/src/constants.rs)
                // for detecting a duel session from its id.
                const isTrainingDuelSession = event.state.sessionId.startsWith('party_training_');
                const lastSummary =
                  event.state.lastSessionRecap ??
                  (isTrainingDuelSession
                    ? null
                    : localStorage.getItem('game-session-last-summary'));
                if (lastSummary && !alreadyHasRecap) {
                  state.addMessage({
                    id: `recap-${Date.now()}`,
                    senderId: GAME_MASTER.charId,
                    senderName: GAME_MASTER.name,
                    avatarUrl: GAME_MASTER.avatarUrl,
                    avatarEmoji: GAME_MASTER.avatarEmoji,
                    content: `**Previously...**\n\n${lastSummary}`,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                  });
                  // Don't remove from localStorage — next session's summary overwrites it
                  // naturally, and the alreadyHasRecap guard prevents duplicates on reconnect.

                  // Do NOT optimistically show the thinking indicator on resume.
                  // A resumed session in WAIT_FOR_PLAYER_INTENT may run no round at
                  // all, so nothing would arrive to clear an optimistic indicator and
                  // it would stick on "thinking..." until the player acts. (A roleplay
                  // resume CAN fire a player-less round through the backend's
                  // opening-NPC poll window, but that is not guaranteed and combat
                  // resumes never do.) The backend is the source of truth: it sends
                  // `round_started` when a round is actually executing (including to a
                  // client reconnecting mid-round, see connection_management.rs), which
                  // flips the indicator on accurately. `sessionStartRound` is still set
                  // in the main `set()` above, preserving the replay-vs-live gating.
                }
              }

              // R1 loading indicator handled by isWaitingForRound + TypingIndicator in GameChatContent

              // Browser notification for turn change (per spec lines 1401, 1416)
              if (
                typeof window !== 'undefined' &&
                !document.hasFocus() &&
                event.state.currentTurn &&
                event.state.currentTurn !== previousTurn
              ) {
                // Use O(1) map lookup
                const currentCharacter = partyMembersMap[event.state.currentTurn];
                if (currentCharacter) {
                  notificationService.notify("It's Your Turn!", {
                    body: `${currentCharacter.name}'s turn`,
                    type: 'message',
                  });
                }
              }
              break;
            }

            case 'scene_transition':
              console.log('[GameStore] Scene transition received', {
                toScene: event.toScene,
                hasDescription: !!event.toScene?.description,
                hasImageUrl: !!event.toScene?.imageUrl,
                travelDestinationCount: event.travelDestinations?.length ?? 0,
              });
              set({
                currentScene: event.toScene,
                currentSceneId: event.toScene?.sceneId ?? null,
                inGameTime: event.inGameTime ?? get().inGameTime,
                isSceneLoading: false,
                sceneItems: event.sceneItems ?? [],
                sceneInteractables: event.sceneInteractables ?? [],
                // Take destinations from the transition payload (Bug 3B). The
                // backend now bundles the new scene's reachable list on the
                // same WS frame as the scene swap, so the /travel menu reflects
                // the destination scene's exits/paths immediately. Falling
                // through to [] would create the same "menu empty until the
                // next RoundResult" hole the hoist set out to close.
                travelDestinations: event.travelDestinations ?? [],
                activeBoons: event.activeBoons ?? [],
                // Clear scene chars from prior scene; the next round_result
                // repopulates from observable_non_party_members. Without this,
                // prior-scene NPCs (e.g. Bram in meadow) linger in the panel
                // for ~1 round after travel because round_result's sync block
                // only runs when obsParty/obsNonParty is non-empty (gameStore
                // line 1573), so a destination with no NPCs (e.g. mountain
                // trail) would never trim them.
                sceneCharacters: [],
                // A square staged on the old scene's board means nothing on
                // the new one, and ChatPanel stamps the outgoing rider with
                // the CURRENT scene id, so a leftover path would pass the
                // server's stale-scene guard wearing the new scene's name.
                stagedMove: null,
                // 3D world: hard cancel, prior scene's transcript/entities
                // are meaningless on the new board (spec: hard cancel on
                // scene transition). The next round_result repopulates.
                pendingPresentationRound: null,
                worldEntitySnapshot: null,
              });
              // Scene image + description as Game Master message.
              // Always show the base scene image on transition — the encounter
              // image (with monsters) is shown separately when combat triggers.
              {
                // Stable on (toScene, server timestamp) — a replayed
                // scene_transition (reconnect drain / dispatcher race) carries
                // the same source event, hence the same id; dedup catches it.
                // Returning to the same scene later is a different event
                // (different timestamp) and gets its own bubble.
                //
                // `addMessage` doesn't id-dedup (unlike `enqueueMessages`), so
                // we guard explicitly here — without it, a replay would append a
                // second bubble even with a stable id.
                const transitionId = `scene-transition-${event.toScene.sceneId}-${event.timestamp}`;
                const alreadyShownTransition = get().messages.some((m) => m.id === transitionId);
                if (!alreadyShownTransition) {
                  // GENUINE (non-replayed) transition only: end the prior scene's
                  // DM stream — sweep its live `dm-narrative-stream-*` bubble and
                  // drop the in-flight accumulator. The orphan stream is what
                  // pins `hasLiveStreaming` in the chat view (its 100ms
                  // scroll-to-bottom interval never clears), which hangs the
                  // renderer beside the new scene's looping inline video (BUG-6
                  // scene-transition-mid-stream race). MUST stay gated on the
                  // dedup: a replay landing AFTER the new scene already started
                  // streaming would otherwise wipe the NEW scene's live bubble +
                  // accumulator, and later chunks would rebuild it missing
                  // earlier text. `streamingRound` is left untouched (preview's
                  // #204 late-chunk drop guard). The round's full narration still
                  // arrives via preview/round_result.
                  // Suppress live streaming for the round whose narration
                  // coincides with this transition, but ONLY when the new scene
                  // mounts a looping video (the freeze precondition). The round's
                  // continuing/just-starting `narrative_stream` chunks would
                  // otherwise rebuild a live bubble beside that video; its
                  // narration still arrives as a settled `narrative_preview`
                  // bubble. Image-only / no-media scenes keep streaming normally.
                  set((s) => ({
                    messages: withoutStreamBubbles(s.messages),
                    streamingNarrative: null,
                    streamingNarrativeRound: null,
                    suppressStreamAfterSceneVideo: sceneRendersLoopingVideo(event.toScene),
                  }));
                  state.addMessage(
                    buildSceneImageMessage(event.toScene, transitionId, {
                      preferEncounter: false,
                    })
                  );
                }
              }
              break;

            case 'scene_image_generated':
              // NOT YET ENABLED — dynamic scene image generation is disabled on the backend
              // (trigger_scene_image_if_changed is not called). Currently using pre-generated
              // module images only. This handler remains for when dynamic gen is re-enabled.
              console.log('[GameStore] Scene image generated', {
                sceneName: event.scene_name,
                imageUrl: event.image_url,
                imageId: event.image_id,
                durationMs: event.duration_ms,
              });
              // Update current scene with the new image URL
              set((state) => ({
                currentScene: state.currentScene
                  ? { ...state.currentScene, imageUrl: event.image_url }
                  : {
                      sceneId: state.currentSceneId ?? '',
                      name: formatSceneName(event.scene_name),
                      imageUrl: event.image_url,
                    },
              }));
              break;

            case 'combat_highlight_ready': {
              // Generation is detached from round processing. Surface the clip
              // immediately when it lands, flushing any older paced round
              // bubbles first so the asynchronous highlight cannot be buried
              // behind the reveal queue. Stable server ids make reconnect
              // buffer replays idempotent.
              const id = `combat-highlight-${event.id}`;
              const current = get();
              const alreadyRendered =
                current.messages.some((message) => message.id === id) ||
                current.messageQueue.some((message) => message.id === id);
              if (!alreadyRendered) {
                current.addMessage({
                  id,
                  senderId: GAME_MASTER.charId,
                  senderName: GAME_MASTER.name,
                  avatarUrl: GAME_MASTER.avatarUrl,
                  avatarEmoji: GAME_MASTER.avatarEmoji,
                  content: `[COMBAT_HIGHLIGHT:${event.video_url}]\n\n${event.action_description}`,
                  timestamp: new Date().toISOString(),
                  type: 'dm',
                  meta: {
                    generation_id: event.id,
                    round: event.round,
                    char_id: event.char_id,
                    direction: event.direction,
                    cost_pm: event.cost_pm,
                  },
                });
              }
              break;
            }

            case 'party_image_generated':
              // Party image generated (after membership change) — update party panel image
              console.log('[GameStore] Party image generated', {
                imageUrl: event.image_url,
                imageId: event.image_id,
                durationMs: event.duration_ms,
              });
              set({ partyImageUrl: event.image_url });
              break;

            case 'character_image_updated': {
              // A PC's portrait/avatar finished generating AFTER a one-click
              // quick-start already entered the game. Patch that character's art
              // in place so the avatar updates live instead of staying on the
              // emoji fallback for the whole first session. The rendered avatars
              // read the derived slices (partyMembers / sceneCharacters /
              // partyMembersMap), and round_result rebuilds `characters` from
              // them, so patch all four the same way a resource update does.
              const { char_id, portrait_url, avatar_url } = event;
              const applyArt = (c: GameCharacterInfo): GameCharacterInfo =>
                c.charId === char_id
                  ? {
                      ...c,
                      portraitUrl: portrait_url ?? c.portraitUrl,
                      avatarUrl: avatar_url ?? c.avatarUrl,
                    }
                  : c;
              set((state) => {
                const partyMembersMap = { ...state.partyMembersMap };
                if (partyMembersMap[char_id]) {
                  partyMembersMap[char_id] = applyArt(partyMembersMap[char_id]);
                }
                return {
                  characters: state.characters.map(applyArt),
                  partyMembers: state.partyMembers.map(applyArt),
                  sceneCharacters: state.sceneCharacters.map(applyArt),
                  partyMembersMap,
                };
              });
              break;
            }

            // =====================================================================
            // Real-time narrative token streaming (during Stage 2 LLM generation)
            // =====================================================================

            case 'narrative_stream': {
              const state = get();
              const streamId = `dm-narrative-stream-${event.round}`;

              // #204 race guard: preview/round_result for this round already
              // landed (sets streamingRound; clears streamingNarrative). A late
              // stream chunk — including the terminal `done: true` frame —
              // arriving after preview would otherwise pass !streamingNarrative
              // and create a fresh "DM" bubble alongside the already-rendered
              // Polymyth one (or trigger a no-op setState in the done branch).
              // Drop the chunk — the full narrative is already in the
              // preview/result bubble.
              // Server: preview + stream chunks are emitted from separate
              // tokio::spawn tasks (engine_actor.rs:254-315), so wire ordering
              // can flip.
              const previewAlreadyHandledThisRound = state.streamingRound === event.round;
              if (previewAlreadyHandledThisRound) {
                break;
              }

              // BUG-6 late-chunk-after-round_result backstop: round_result resets
              // `streamingRound` to null (so the #204 guard above no longer fires)
              // but advances `lastResolvedRound`. A chunk for an already-resolved
              // round is redundant — the full narration anchored in the
              // preview/result bubble — and must NOT recreate a live
              // `dm-narrative-stream-${round}` bubble beside a freshly-mounted scene
              // video (the @llm-ui freeze/OOM path). The engine_actor now awaits the
              // stream forwarder before Success, so this should be unreachable; this
              // is the FE guard against any future ordering regression (the OOM is
              // silent until the StreamingMarkdown watchdog fires).
              if (event.round <= state.lastResolvedRound) {
                break;
              }

              // done: true — LLM stream finished, hide cursor AND drop the
              // in-flight accumulator. A `done` frame means no DM stream is
              // live anymore, so `streamingNarrative`/`streamingNarrativeRound`
              // must not linger: if they did, the NEXT round's first chunk
              // would see a non-null `streamingNarrative`, mis-route into the
              // subsequent-chunk branch, and append to a `dm-narrative-stream-${nextRound}`
              // id that doesn't exist yet — so that round's narration never
              // renders and the accumulator grows across rounds. `streamingRound`
              // is left untouched (preview's #204 late-chunk guard).
              if (event.done) {
                set({
                  messages: state.messages.map((msg) =>
                    msg.id === streamId && msg.isStreaming ? { ...msg, isStreaming: false } : msg
                  ),
                  streamingNarrative: null,
                  streamingNarrativeRound: null,
                });
                break;
              }

              // BUG-6 scene-transition-mid-stream race: a genuine scene_transition
              // into a looping-video scene set `suppressStreamAfterSceneVideo`. Drop
              // this round's live chunks (no visible bubble) until the
              // preview/round_result anchors the narration as a settled bubble. A
              // live `dm-narrative-stream-*` bubble (re)created here would sit at
              // `isStreaming: true` beside the just-mounted video and pin
              // `hasLiveStreaming`, hanging the renderer. The full narration still
              // renders via `narrative_preview` (`dm-narrative-preview-*`).
              if (state.suppressStreamAfterSceneVideo) {
                break;
              }

              if (!state.streamingNarrative) {
                // First chunk — start the streaming message.
                // Clear the thinking indicator UNLESS this chunk is for a round
                // that was behind the warmup gate (buffered replay). Replay chunks
                // keep the indicator up until a live next-round chunk arrives.
                const isBufferedReplay =
                  state.sessionStartRound != null && event.round <= state.sessionStartRound;
                set({
                  ...(isBufferedReplay
                    ? {}
                    : { isWaitingForRound: false, sessionStartRound: null }),
                  streamingNarrative: event.chunk,
                  streamingNarrativeRound: event.round,
                });
                // Add a streaming DM message to the chat.
                // Sweep EVERY existing `dm-narrative-stream-*` bubble first —
                // including this round's own id — then append the fresh one.
                // Only one DM stream is ever live, so any survivor is stale:
                // prior-round orphans whose `done`/preview cleanup never landed
                // (interrupted or superseded rounds during idle-round churn),
                // OR a same-id bubble left when a wrong-round preview cleared
                // `streamingNarrative` without removing it — re-entering this
                // branch then would otherwise append a duplicate id and collide
                // React keys. Sweeping all + appending one keeps ids unique and
                // caps unbounded orphan growth (BUG-6c).
                const streamMessage: GameUIMessage = {
                  id: streamId,
                  senderName: 'DM',
                  content: event.chunk,
                  timestamp: new Date(),
                  type: 'dm' as ChatMessageType,
                  isStreaming: true,
                };
                // Prior-round bubbles still queued for paced reveal are OLDER
                // than this live stream; dump them (finalized) ahead of it or
                // they'd pop out BELOW the next round's narration (same
                // ordering invariant as addMessage; mobile flushes on
                // NarrativeStreamMessage for the same reason).
                set((prev) => {
                  const dumped = prev.messageQueue.map((m) => ({
                    ...m,
                    isStreaming: false,
                    isQueuedReveal: false,
                  }));
                  return {
                    messages: capMessages([
                      ...withoutStreamBubbles(prev.messages),
                      ...dumped,
                      streamMessage,
                    ]),
                    messageQueue: [],
                    unreadCount: prev.unreadCount + dumped.length,
                  };
                });
              } else {
                // Subsequent chunks — append to existing streaming message
                const newNarrative = state.streamingNarrative + event.chunk;
                set({ streamingNarrative: newNarrative });
                // Update the streaming message in-place
                const updatedMessages = state.messages.map((msg) =>
                  msg.id === streamId ? { ...msg, content: newNarrative } : msg
                );
                set({ messages: updatedMessages });
              }
              break;
            }

            // =====================================================================
            // Early narrative streaming — preview arrives ~10-15s before round_result
            // =====================================================================

            case 'narrative_preview': {
              // Track which round's preview is active (for ghost cleanup on error).
              // Remove streaming message if it exists (preview replaces with full content).
              // Clear the R1 thinking indicator UNLESS this preview is for a round
              // that was behind the warmup gate (buffered replay — the "Previously..."
              // recap + R1 content all arrive in one burst and the user should still
              // see the indicator until the NEXT live round streams).
              const streamMsgId = `dm-narrative-stream-${event.round}`;
              const currentMessages = get().messages;
              const filteredMessages = currentMessages.filter((m) => m.id !== streamMsgId);
              const gateState = get();
              const isPreviewBufferedReplay =
                gateState.sessionStartRound != null && event.round <= gateState.sessionStartRound;
              set({
                streamingRound: event.round,
                ...(isPreviewBufferedReplay
                  ? {}
                  : { isWaitingForRound: false, sessionStartRound: null }),
                streamingNarrative: null,
                streamingNarrativeRound: null,
                // Narration for the post-transition round now renders as a settled
                // preview bubble — lift the scene-video stream suppression so the
                // NEXT round streams live again.
                suppressStreamAfterSceneVideo: false,
                messages: filteredMessages,
              });
              // Narrative + char_actions from Stage 2 (before StateTransformer runs).
              // Queue for progressive reveal — same UX as round_result messages.
              // Bubble ids are stable on (round, sender, idx) so a re-dispatched
              // preview (replay, reconnect drain, dispatcher race) dedups against
              // the already-queued bubbles via enqueueMessages' id check, not just
              // for stable-id action bubbles. See enqueueMessages above.
              const previewMessages: GameUIMessage[] = [];
              const previewState = get();
              const previewPartyMap = previewState.partyMembersMap;

              // Character action bubbles.
              // Prefer server-supplied avatar fields — sceneCharacters may not
              // yet include a newly-spawned NPC (e.g. Theron appearing in the
              // tomb) at preview time, so the local lookup misses and the
              // bubble would otherwise fall back to DEFAULT_PLAYER_AVATAR.
              if (event.character_actions && event.character_actions.length > 0) {
                for (const action of event.character_actions) {
                  const charInfo =
                    previewPartyMap[action.char_id] ??
                    previewState.sceneCharacters.find((c) => c.charId === action.char_id);
                  previewMessages.push({
                    id: action.id,
                    senderId: action.char_id,
                    senderName: charInfo?.name ?? action.char_name,
                    avatarUrl: action.avatar_url ?? charInfo?.avatarUrl,
                    avatarEmoji: action.avatar_emoji ?? charInfo?.avatarEmoji,
                    senderPortraitUrl: action.portrait_url ?? charInfo?.portraitUrl,
                    content: action.description,
                    timestamp: new Date().toISOString(),
                    type: 'user',
                    bubbleColor: pickChatBubbleColor(action.char_id),
                  });
                }
                // Polymyth 2.0: spoken lines ride into the 3D world as
                // bubbles; the stable action id dedups the round_result
                // re-delivery of the same lines. Buffered replays (warmup
                // drains, reconnects) are history, not live speech: the
                // module-scope id ring is empty after a reload, so the
                // replay gate is what actually keeps them silent.
                if (!isPreviewBufferedReplay) {
                  get().queueWorldSpeech(
                    event.character_actions.flatMap((a) => {
                      const line = extractSpokenLine(a);
                      if (line)
                        return [
                          { charId: a.char_id, text: line, dedupeId: a.id, round: event.round },
                        ];
                      // No speech: a leading gesture verb still animates
                      // (the wave without words). Speech always wins.
                      const emote = extractEmoteGesture(a);
                      return emote
                        ? [
                            {
                              charId: a.char_id,
                              text: '',
                              emote,
                              dedupeId: a.id,
                              round: event.round,
                            },
                          ]
                        : [];
                    })
                  );
                }
              }

              // Private messages (whispers)
              if (event.my_private_messages && event.my_private_messages.length > 0) {
                for (const [idx, pm] of event.my_private_messages.entries()) {
                  const senderInfo =
                    previewPartyMap[pm.from_char_id] ??
                    previewState.sceneCharacters.find((c) => c.charId === pm.from_char_id);
                  previewMessages.push({
                    id: `whisper-preview-${event.round}-${pm.from_char_id}-${idx}`,
                    senderId: pm.from_char_id,
                    senderName: senderInfo?.name ?? pm.from_char_name,
                    avatarUrl: pm.avatar_url ?? senderInfo?.avatarUrl,
                    avatarEmoji: pm.avatar_emoji ?? senderInfo?.avatarEmoji,
                    senderPortraitUrl: pm.portrait_url ?? senderInfo?.portraitUrl,
                    content: pm.message,
                    timestamp: new Date().toISOString(),
                    type: 'whisper',
                    bubbleColor: pickChatBubbleColor(pm.from_char_id),
                  });
                }
              }

              // Private narrative (my_char_narrative)
              if (event.my_char_narrative) {
                const myCharId = previewState.playerCharacterId;
                const playerChar = myCharId ? previewPartyMap[myCharId] : undefined;
                previewMessages.push({
                  id: `private-narrative-preview-${event.round}`,
                  senderId: myCharId ?? 'self',
                  senderName: playerChar?.name ?? 'You',
                  avatarUrl: playerChar?.avatarUrl,
                  avatarEmoji: playerChar?.avatarEmoji,
                  senderPortraitUrl: playerChar?.portraitUrl,
                  content: event.my_char_narrative,
                  timestamp: new Date().toISOString(),
                  type: 'whisper',
                  bubbleColor: pickChatBubbleColor(myCharId ?? 'self'),
                });
              }

              // DM narrative
              if (event.narrative) {
                previewMessages.push({
                  id: `dm-narrative-preview-${event.round}`,
                  senderId: GAME_MASTER.charId,
                  senderName: GAME_MASTER.name,
                  avatarUrl: GAME_MASTER.avatarUrl,
                  avatarEmoji: GAME_MASTER.avatarEmoji,
                  content: event.narrative,
                  timestamp: new Date().toISOString(),
                  type: 'dm',
                });
              }

              if (previewMessages.length > 0) {
                get().enqueueMessages(previewMessages);
              }
              break;
            }

            // Round result — contains narrative, state changes, and character actions
            case 'round_result': {
              const resultEvent = event as RoundResultMessage;

              // Snapshot hostile presence BEFORE state update for encounter video switching.
              // Compared against post-update state to detect hostiles appearing/disappearing.
              const prevHadLivingHostiles = get().sceneCharacters.some(
                (c) =>
                  c.healthState !== 'dead' && (c.type === 'monster' || c.attitude === 'hostile')
              );

              // 1. First round result arrived — show game UI + apply state changes atomically.
              // Single set() prevents flash of stale data between gameInitialized and state update.
              // Also clears streamingRound — preview phase complete, real results arrived.
              set((prevState) => {
                // Remove startup placeholders + preview whispers/private-narratives for this round.
                // round_result carries the final versions (Stage 3 may differ from preview).
                // Action previews are NOT removed — they dedup by stable server-generated IDs.
                // Must filter both messages AND messageQueue (preview items may still be queued).
                const round = resultEvent.round;
                const isStalePreview = (id: string) =>
                  id.startsWith(`whisper-preview-${round}-`) ||
                  id === `private-narrative-preview-${round}` ||
                  id === `dm-narrative-preview-${round}`;
                // Reset revealingMessageId if the mid-reveal message is one we're removing —
                // prevents permanent queue stall (advanceQueue waits for finishReveal that never fires).
                const revealingStale =
                  prevState.revealingMessageId !== null &&
                  isStalePreview(prevState.revealingMessageId);
                // Intentionally do NOT apply isStalePreview to messages: streamed preview messages
                // must remain visible in the chat history. When a round_result arrives, the backend
                // sends ONLY the post-round additions (ambient damage, detection text) in
                // RoundResult.narrative — never the LLM stream again. So the preview message in
                // messages is the narrative and the additions are a new message; no duplicate occurs.
                // Removing stale previews from messages would make them visually disappear mid-read.
                // Finalize the blinking cursor on EVERY still-streaming
                // `dm-narrative-stream-*` bubble, not just this round's. This
                // round arriving without a preceding preview (degraded mode, fast
                // rounds) is the common case; but a prior-round orphan (a
                // label-mismatched stream whose `done`/preview cleanup never
                // landed) would otherwise stay `isStreaming: true` and pin
                // `hasLiveStreaming` in the chat view — and this round may enqueue
                // a looping inline combat-encounter video (2g below), which hangs
                // the renderer (BUG-6). Finalizing (not removing) keeps the text.
                //
                // finalizeStreamBubbles only clears `dm-narrative-stream-*`. But
                // when the message mid-reveal is a RETAINED preview (revealingStale
                // — e.g. `dm-narrative-preview-${round}`, kept in messages above),
                // nulling revealingMessageId below would orphan it at
                // `isStreaming: true` forever (no owner left to call finishReveal),
                // pinning `hasLiveStreaming` + the 100ms auto-scroll and risking the
                // same BUG-6 freeze. Finalize that specific bubble in the same pass.
                const messages = finalizeStreamBubbles(prevState.messages).map((msg) =>
                  revealingStale && msg.id === prevState.revealingMessageId
                    ? { ...msg, isStreaming: false, isQueuedReveal: false }
                    : msg
                );
                // messageQueue may still hold unshown preview items. Whisper and
                // private-narrative previews ARE re-sent by round_result (2b/2c),
                // so drop the stale queued copies. The DM narrative is NOT re-sent
                // when post-round additions are empty: once `preview_sent_to` is
                // set, `build_player_round_message` puts only `narrative_additions`
                // (often "") in RoundResult.narrative. So a queued-but-unrevealed
                // `dm-narrative-preview-${round}` must be KEPT, or the round renders
                // with NO GM narration (#347 silent-round regression: the preview
                // carries the full narrative and lives in the queue until
                // advanceQueue reveals it, which stalls while the chat cell is
                // unmounted). It reveals normally; the 2e guard below dedups the
                // rare desync where round_result re-delivers the same full text.
                const messageQueue = prevState.messageQueue.filter(
                  (msg) => msg.id === `dm-narrative-preview-${round}` || !isStalePreview(msg.id)
                );
                let updatedPartyMembers = prevState.partyMembers;
                let updatedSceneCharacters = prevState.sceneCharacters;
                let updatedPartyMembersMap: Record<string, GameCharacterInfo> = {
                  ...prevState.partyMembersMap,
                };

                // When full char data is present, skip deltas for the player's own character
                // (full data is authoritative — deltas would be redundant)
                const perm = resultEvent.my_character_permanent;
                const sess = resultEvent.my_character_session;
                const hasFullCharData =
                  perm &&
                  sess &&
                  typeof perm === 'object' &&
                  'max_hp' in perm &&
                  typeof sess === 'object' &&
                  'hp' in sess;
                const playerCharId = prevState.playerCharacterId;

                // Process state changes from the array format
                const changesByCharId: Record<string, Partial<CharacterStateChange>> = {};
                for (const change of resultEvent.changes) {
                  // Skip player's own char when we have authoritative full data.
                  // The two position-related changes are the exceptions, because
                  // the grid square is the one thing the full data does NOT
                  // carry: `combat_position` is `#[serde(skip)]` on the Rust
                  // session, so it is absent from `my_character_session` and
                  // `syncPlayerCharData` deliberately never touches `position`.
                  // `grid_position` moves the token, `combat_badges_cleared`
                  // drops it when the fight ends; without both, the player was
                  // the only character whose token never moved and never left.
                  if (
                    hasFullCharData &&
                    playerCharId &&
                    change.char_id === playerCharId &&
                    change.change_type !== 'grid_position' &&
                    change.change_type !== 'combat_badges_cleared'
                  )
                    continue;
                  const existing = changesByCharId[change.char_id] || {};
                  switch (change.change_type) {
                    case 'hp_change':
                      existing.hpChange = (existing.hpChange ?? 0) + (change.value as number);
                      break;
                    case 'hp_set':
                      existing.hp = change.value as number;
                      break;
                    case 'temp_hp':
                      existing.tempHp = change.value as number;
                      break;
                    case 'condition_added': {
                      existing.conditions = existing.conditions ?? { add: [], remove: [] };
                      const condName =
                        typeof change.value === 'string'
                          ? change.value
                          : (change.value as { condition: string }).condition;
                      existing.conditions.add = [...(existing.conditions.add ?? []), condName];
                      break;
                    }
                    case 'condition_removed':
                      existing.conditions = existing.conditions ?? { add: [], remove: [] };
                      existing.conditions.remove = [
                        ...(existing.conditions.remove ?? []),
                        change.value as string,
                      ];
                      break;
                    case 'spell_slot_used': {
                      const slot = change.value as { level: number; count: number };
                      existing.spellSlotsUsed = existing.spellSlotsUsed ?? {};
                      existing.spellSlotsUsed[slot.level] =
                        (existing.spellSlotsUsed[slot.level] ?? 0) + slot.count;
                      break;
                    }
                    case 'concentration_started': {
                      const conc = change.value as { spell: string };
                      existing.concentration = conc.spell;
                      break;
                    }
                    case 'concentration_ended':
                      existing.concentration = null;
                      break;
                    case 'death_save':
                      existing.deathSave = change.value as { success: boolean; critical: boolean };
                      break;
                    case 'cover_change':
                      existing.cover = change.value as 'none' | 'half' | 'three_quarters' | 'total';
                      break;
                    case 'exhaustion_change':
                      existing.exhaustionLevel = change.value as number;
                      break;
                    case 'item_acquired': {
                      existing.itemsAcquired = existing.itemsAcquired ?? [];
                      existing.itemsAcquired.push(
                        change.value as {
                          item_name: string;
                          item_id: string;
                          quantity?: number;
                          magical?: boolean;
                        }
                      );
                      break;
                    }
                    case 'item_removed':
                      existing.itemsRemoved = existing.itemsRemoved ?? [];
                      existing.itemsRemoved.push(change.value as string);
                      break;
                    case 'gold_change':
                      existing.goldChange = (existing.goldChange ?? 0) + (change.value as number);
                      break;
                    case 'xp_gain':
                      existing.xpGained = (existing.xpGained ?? 0) + (change.value as number);
                      break;
                    case 'feature_used':
                      existing.featuresUsed = existing.featuresUsed ?? [];
                      existing.featuresUsed.push(change.value as string);
                      break;
                    case 'class_resource_used': {
                      const v = change.value as { resource: string; count: number };
                      existing.classResourcesUsed = existing.classResourcesUsed ?? {};
                      existing.classResourcesUsed[v.resource] =
                        (existing.classResourcesUsed[v.resource] ?? 0) + v.count;
                      break;
                    }
                    case 'item_charge_used': {
                      const v = change.value as { item_id: string; count: number };
                      existing.itemChargesUsed = existing.itemChargesUsed ?? {};
                      existing.itemChargesUsed[v.item_id] =
                        (existing.itemChargesUsed[v.item_id] ?? 0) + v.count;
                      break;
                    }
                    case 'sorcery_points_delta':
                      existing.sorceryPointsDelta =
                        (existing.sorceryPointsDelta ?? 0) + (change.value as number);
                      break;
                    case 'position_change':
                      // Narrative location string ("pushed 10 feet back"), not a
                      // grid square. `grid_position` below is the map's channel.
                      existing.position = { location: change.value as string };
                      break;
                    case 'grid_position':
                      existing.gridPosition = change.value as { x: number; y: number };
                      break;
                    // Party-panel combat badges are absolute values, so the last
                    // message for a character in a round wins rather than
                    // accumulating.
                    case 'heroic_inspiration':
                      existing.heroicInspiration = change.value as boolean;
                      break;
                    case 'death_save_tally':
                      existing.deathSaveTally = change.value as {
                        successes: number;
                        failures: number;
                      };
                      break;
                    case 'last_saving_throw':
                      existing.lastSavingThrow = change.value as LastSavingThrow;
                      break;
                    case 'crit_markers':
                      existing.critMarkers = change.value as { dealt: boolean; taken: boolean };
                      break;
                    case 'combat_badges_cleared':
                      existing.combatBadgesCleared = true;
                      break;
                    // Session-level change types — no per-character state update needed
                    case 'quest_flag':
                    case 'quest_objective':
                    case 'reputation_change':
                    case 'discovery':
                    case 'scene_change':
                      break;
                  }
                  changesByCharId[change.char_id] = existing;
                }

                // Apply deltas to party members (player's own char handled separately via full data)
                updatedPartyMembers = prevState.partyMembers.map((member) => {
                  const changes = changesByCharId[member.charId];
                  // The own char is replaced wholesale by `syncPlayerCharData`
                  // below, so its deltas are redundant. Position is the
                  // exception the accumulate loop above lets through, and it
                  // survives here because `syncPlayerCharData` never writes
                  // `position`. Nothing else is applied: the session blob is
                  // authoritative for every other field, badges included.
                  if (hasFullCharData && member.charId === playerCharId) {
                    // Combat over: drop the token. Checked first so it wins over
                    // a same-round position, matching the Rust precedence.
                    if (changes?.combatBadgesCleared) {
                      const cleared = { ...member, position: undefined };
                      updatedPartyMembersMap[member.charId] = cleared;
                      return cleared;
                    }
                    if (changes?.gridPosition === undefined) return member;
                    const moved = { ...member, position: changes.gridPosition };
                    updatedPartyMembersMap[member.charId] = moved;
                    return moved;
                  }
                  if (!changes) return member;
                  const updated = applyCharacterStateChange(
                    member,
                    changes as CharacterStateChange
                  );
                  updatedPartyMembersMap[member.charId] = updated;
                  return updated;
                });

                // Apply to scene characters
                updatedSceneCharacters = prevState.sceneCharacters.map((char) => {
                  const changes = changesByCharId[char.charId];
                  if (!changes) return char;
                  const updated = applyCharacterStateChange(char, changes as CharacterStateChange);
                  if (updatedPartyMembersMap[char.charId]) {
                    updatedPartyMembersMap[char.charId] = updated;
                  }
                  return updated;
                });

                // Update encounter type from session_changes
                let encounterType = prevState.encounterType;
                const sessionChanges = resultEvent.session_changes;
                if (sessionChanges?.encounterType) {
                  encounterType = sessionChanges.encounterType as EncounterType;
                } else if (
                  sessionChanges?.combatResolution &&
                  prevState.encounterType === 'combat'
                ) {
                  encounterType = 'exploration';
                } else if (sessionChanges?.combatTrigger) {
                  encounterType = 'combat';
                }

                // Sync scene characters from observable views (new spawns, deaths, health state updates)
                // Observable views represent what the player can see about OTHER characters in the scene
                const obsParty = resultEvent.observable_party_members ?? [];
                const obsNonParty = resultEvent.observable_non_party_members ?? [];

                // Warmup-gate replay guard: if this round_result is for a round
                // that was behind the warmup gate (drained burst), keep the
                // thinking indicator up — we're waiting for the NEXT live round.
                // Only live rounds (round > sessionStartRound) clear it.
                //
                // Also gates the roster trim below: `release_warmup_gate` builds
                // a FRESH `game_state` and sends it BEFORE draining the buffered
                // frames, so a replayed round carries an older roster than the
                // snapshot we already hold and trimming on it would delete
                // characters that are present now.
                const isResultBufferedReplay =
                  prevState.sessionStartRound != null &&
                  resultEvent.round <= prevState.sessionStartRound;

                // Who is here, per the server. NOT derived from the observable
                // views: those are filtered per observer by `is_visible_to_observer`,
                // so a monster that turns Invisible drops out of them while
                // remaining a legal attack target (SRD 5.2 gives the attacker
                // Disadvantage, it does not remove the target). Trimming on them
                // made concealment indistinguishable from departure and deleted an
                // enemy from the /attack picker mid-fight.
                //
                // Skipped when the field is absent (a payload predating the roster,
                // treat as "don't trim") and when playerCharId is null (rehydration
                // race: partyMembers may have persisted from a prior session before
                // game_state restores it, and the filter would drop everyone).
                const roster =
                  playerCharId !== null && !isResultBufferedReplay ? resultEvent.roster : undefined;
                const rosterIds = roster ? new Set(roster.map((e) => e.charId)) : null;
                const rosterPartyIds = roster
                  ? new Set(roster.filter((e) => e.isPartyMember).map((e) => e.charId))
                  : null;

                // Drop party members the roster no longer lists as party, keeping
                // the player unconditionally. This is what un-pins Elara after she
                // auto-leaves at Aldwin's hut in Goldvale — she used to stay in the
                // party panel at her pre-departure HP for the rest of the session.
                //
                // A member the roster demoted but still lists is carried across
                // to the scene rather than dropped: `newSceneChars` below is fed
                // only from `obsNonParty`, so someone who leaves the party in a
                // round she isn't perceivable in (Hidden, Invisible) would fall
                // out of both lists and vanish despite still being there.
                const demotedToScene: GameCharacterInfo[] = [];
                if (rosterPartyIds) {
                  updatedPartyMembers = updatedPartyMembers.filter((m) => {
                    if (m.charId === playerCharId || rosterPartyIds.has(m.charId)) return true;
                    if (rosterIds!.has(m.charId)) {
                      demotedToScene.push({ ...m, isPartyMember: false });
                    }
                    return false;
                  });
                  updatedPartyMembersMap = Object.fromEntries(
                    Object.entries(updatedPartyMembersMap).filter(
                      ([id]) => id === playerCharId || rosterPartyIds.has(id)
                    )
                  );
                }

                // Deliberately unconditional. Both observable lists are omitted
                // when empty, and empty is meaningful: with both empty the
                // refresh and add-new passes below are no-ops, which is correct
                // — the player perceives nobody. Presence is decided by the
                // roster above, not here.
                {
                  // Refresh existing party members from the round's observable
                  // view so HP/maxHp/conditions stay current even on rounds
                  // that emit no `state_changes` deltas for them — e.g. long
                  // rest restores HP server-side but emits only the toast
                  // `rest_completed`, leaving the panel stuck at last-known
                  // critical/bloodied HP until the next combat tick.
                  //
                  // The player's own char is excluded — `my_character_*`
                  // below replaces it with the authoritative full snapshot.

                  // A character the player cannot perceive this round holds no
                  // square on the tactical map. `is_visible_to_observer` drops a
                  // Hidden or Invisible character from the observable views on
                  // purpose, while the roster keeps the entry because concealment
                  // is not departure. So the entry stays and only the grid
                  // position goes: without this the token froze at its last-seen
                  // cell, showing the player exactly where an Invisible enemy
                  // stood and naming it in the board's aria-label roster.
                  //
                  // Not applied to a buffered replay, whose observable set is
                  // older than the snapshot we already hold — the same reason the
                  // roster trim above skips one.
                  const forgetPosition = (c: GameCharacterInfo): GameCharacterInfo =>
                    isResultBufferedReplay || c.position === undefined
                      ? c
                      : { ...c, position: undefined };

                  const obsPartyMap = new Map(obsParty.map((o) => [o.id, o]));
                  updatedPartyMembers = updatedPartyMembers.map((m) => {
                    if (m.charId === playerCharId) return m;
                    const obs = obsPartyMap.get(m.charId);
                    if (!obs) {
                      const unseen = forgetPosition(m);
                      if (unseen !== m) updatedPartyMembersMap[m.charId] = unseen;
                      return unseen;
                    }
                    // Spread fresh over existing so state_changes-only fields
                    // (e.g. spell-slot deltas not surfaced by observable view)
                    // survive while HP/conditions update.
                    const merged = mergeObservedChar(m, obs);
                    updatedPartyMembersMap[m.charId] = merged;
                    return merged;
                  });

                  // Add new chars not yet known (e.g., companion joined)
                  const knownPartyIds = new Set(updatedPartyMembers.map((m) => m.charId));
                  for (const obs of obsParty) {
                    if (!knownPartyIds.has(obs.id)) {
                      const newChar = observableViewToGameCharInfo(obs);
                      updatedPartyMembers = [...updatedPartyMembers, newChar];
                      updatedPartyMembersMap[obs.id] = newChar;
                    }
                  }

                  // Sync non-party scene characters — refresh what we can see,
                  // add newly seen, drop what the ROSTER says has left.
                  const obsNonPartyMap = new Map(obsNonParty.map((o) => [o.id, o]));
                  const existingSceneIds = new Set(updatedSceneCharacters.map((c) => c.charId));
                  const keptSceneChars = updatedSceneCharacters
                    // The roster decides WHICH list holds a character, not just
                    // whether it holds them at all. Presence-only would keep a
                    // scene NPC who just joined the party, and the add-new pass
                    // above has already put him in `partyMembers` — he'd render
                    // twice, at frozen HP under Present, and keep showing up in
                    // the /invite picker. `game_state` partitions the same two
                    // lists on `isPartyMember`; this keeps them disjoint here.
                    //
                    // A character we simply can't see this round (Invisible,
                    // Hidden) is on the roster and not in the party, so it stays.
                    .filter(
                      (c) =>
                        !rosterIds || (rosterIds.has(c.charId) && !rosterPartyIds!.has(c.charId))
                    )
                    .map((c) => {
                      const obs = obsNonPartyMap.get(c.charId);
                      if (!obs) return forgetPosition(c);
                      // Preserve fields from state_changes that observable views don't carry
                      return mergeObservedChar(c, obs);
                    });
                  // Add newly appeared scene chars
                  const newSceneChars = obsNonParty
                    .filter((obs) => !existingSceneIds.has(obs.id))
                    .map(observableViewToGameCharInfo);
                  // Anyone the roster demoted this round who isn't already
                  // accounted for by one of the two passes above.
                  const seenSceneIds = new Set([
                    ...keptSceneChars.map((c) => c.charId),
                    ...newSceneChars.map((c) => c.charId),
                  ]);
                  // Surviving this filter means the round's observable views did
                  // not carry them (anyone they did carry is in `newSceneChars`),
                  // so they are unperceived and lose their square too.
                  const demotedSceneChars = demotedToScene
                    .filter((c) => !seenSceneIds.has(c.charId))
                    .map(forgetPosition);
                  updatedSceneCharacters = [
                    ...keptSceneChars,
                    ...newSceneChars,
                    ...demotedSceneChars,
                  ];
                }

                // Replace player's character with authoritative full data (skipped deltas above)
                if (hasFullCharData && playerCharId) {
                  const fullPerm = perm as CharacterPermanent;
                  const fullSess = sess as CharacterSession;
                  updatedPartyMembers = updatedPartyMembers.map((m) =>
                    m.charId === playerCharId ? syncPlayerCharData(m, fullPerm, fullSess) : m
                  );
                  if (updatedPartyMembersMap[playerCharId]) {
                    updatedPartyMembersMap[playerCharId] = syncPlayerCharData(
                      updatedPartyMembersMap[playerCharId],
                      fullPerm,
                      fullSess
                    );
                  }
                }

                return {
                  streamingRound: null,
                  streamingNarrative: null,
                  streamingNarrativeRound: null,
                  suppressStreamAfterSceneVideo: false,
                  gameInitialized: true,
                  // The round reconciles the counters itself, through
                  // `my_character_session`, so the restore point has done its job.
                  optimisticRestore: null,
                  isWaitingForRound: isResultBufferedReplay ? prevState.isWaitingForRound : false,
                  sessionStartRound: isResultBufferedReplay ? prevState.sessionStartRound : null,
                  messages,
                  messageQueue,
                  revealingMessageId: revealingStale ? null : prevState.revealingMessageId,
                  currentRound: resultEvent.round,
                  // Monotonic high-water mark of resolved rounds; gates late
                  // narrative_stream chunks (see lastResolvedRound docs + the
                  // narrative_stream handler guard).
                  lastResolvedRound: Math.max(prevState.lastResolvedRound, resultEvent.round),
                  encounterType,
                  // A staged board move belongs to the board it was clicked on.
                  // Once a round resolves, everyone has moved and the square is
                  // stale, so drop it rather than let it ride the NEXT message.
                  // Without this it survives combat end (the panel unmounts, so
                  // the "Move staged" hint vanishes with it) and fires as a
                  // phantom rider in the next encounter, spending movement and
                  // risking opportunity attacks the player never asked for.
                  // WorldCanvas clears on playback for the same reason, but it
                  // is not mounted while the 3D world is off.
                  stagedMove: null,
                  lastRoundProcessingMs: resultEvent.processing_time_ms ?? null,
                  inGameTime: resultEvent.in_game_time ?? prevState.inGameTime,
                  // Explicit `null` clears the crown (leader left the party);
                  // an omitted field means a payload that predates the wire
                  // change, so keep whatever we already had.
                  leaderCharacterId:
                    resultEvent.leader_character_id !== undefined
                      ? resultEvent.leader_character_id
                      : prevState.leaderCharacterId,
                  characters: [...updatedPartyMembers, ...updatedSceneCharacters],
                  partyMembers: updatedPartyMembers,
                  sceneCharacters: updatedSceneCharacters,
                  partyMembersMap: updatedPartyMembersMap,
                  sceneItems: resultEvent.scene_items ?? [],
                  sceneInteractables: resultEvent.scene_interactables ?? [],
                  // Absent means "nowhere to travel from here", same as the
                  // observable lists above: the field is
                  // `skip_serializing_if = "Vec::is_empty"` and
                  // `compute_travel_destinations_for_current_scene` rebuilds it
                  // in full every round. Falling back to the previous value
                  // kept offering exits the party can no longer reach.
                  travelDestinations: resultEvent.travel_destinations ?? [],
                  activeBoons: resultEvent.active_boons ?? [],
                  // Append new journal events from this round
                  campaignJournal: (() => {
                    const newEvents = resultEvent.new_journal_events;
                    if (!newEvents?.length) return prevState.campaignJournal;
                    const existing = prevState.campaignJournal ?? {
                      session_recaps: [],
                      events: [],
                      npc_relationships: [],
                      quests: [],
                      discoveries: [],
                      decisions: [],
                      gm_notes: [],
                    };
                    return { ...existing, events: [...existing.events, ...newEvents] };
                  })(),
                  unreadJournalCount:
                    prevState.unreadJournalCount + (resultEvent.new_journal_events?.length ?? 0),
                  // 3D world: stash the round's render transcript for the
                  // canvas, watermark-gated (duplicate deliveries with
                  // round_id <= lastPlayed are dropped; a duplicate of the
                  // round currently pending/playing is dropped too, or the
                  // fresh object identity would restart its playback).
                  // final_entities is also the latest board snapshot: a
                  // remount that misses playback still lands on the right
                  // end state.
                  ...(shouldStashPresentationRound(
                    resultEvent.render_events,
                    prevState.lastPlayedPresentationRound,
                    prevState.pendingPresentationRound?.round_id
                  )
                    ? {
                        pendingPresentationRound: resultEvent.render_events,
                        worldEntitySnapshot: resultEvent.render_events.final_entities,
                      }
                    : {}),
                };
              });

              // 2. Collect all chat messages into a queue for progressive reveal.
              // Bubble ids are (round, sender, idx)-stable so a re-dispatched
              // round_result (replay/reconnect) dedups via enqueueMessages.
              const queuedMessages: GameUIMessage[] = [];
              const currentState = get();
              const partyMap = currentState.partyMembersMap;

              // 2a. Character action bubbles (server-generated stable IDs for dedup)
              // Preview and round_result send the same IDs — skip if already displayed.
              if (resultEvent.character_actions && resultEvent.character_actions.length > 0) {
                const existingIds = new Set([
                  ...currentState.messages.map((m) => m.id),
                  ...currentState.messageQueue.map((m) => m.id),
                ]);
                // Polymyth 2.0 3D bubbles: same stable-id dedup as the 2D
                // queue, shared with the preview delivery of these lines.
                // Same replay gate as the preview site: drained history
                // must not re-bubble after a reload empties the id ring.
                // (Recomputed here: the updater-closure copy is out of
                // scope, and sessionStartRound still marks the drain.)
                const isResultReplayForSpeech =
                  currentState.sessionStartRound != null &&
                  resultEvent.round <= currentState.sessionStartRound;
                if (!isResultReplayForSpeech) {
                  get().queueWorldSpeech(
                    resultEvent.character_actions.flatMap((a) => {
                      const line = extractSpokenLine(a);
                      if (line)
                        return [
                          {
                            charId: a.char_id,
                            text: line,
                            dedupeId: a.id,
                            round: resultEvent.round,
                          },
                        ];
                      // No speech: a leading gesture verb still animates
                      // (the wave without words). Speech always wins.
                      const emote = extractEmoteGesture(a);
                      return emote
                        ? [
                            {
                              charId: a.char_id,
                              text: '',
                              emote,
                              dedupeId: a.id,
                              round: resultEvent.round,
                            },
                          ]
                        : [];
                    })
                  );
                }
                for (const action of resultEvent.character_actions) {
                  if (existingIds.has(action.id)) continue;
                  const charInfo =
                    partyMap[action.char_id] ??
                    currentState.sceneCharacters.find((c) => c.charId === action.char_id);
                  queuedMessages.push({
                    id: action.id,
                    senderId: action.char_id,
                    senderName: charInfo?.name ?? action.char_name,
                    avatarUrl: action.avatar_url ?? charInfo?.avatarUrl,
                    avatarEmoji: action.avatar_emoji ?? charInfo?.avatarEmoji,
                    senderPortraitUrl: action.portrait_url ?? charInfo?.portraitUrl,
                    content: action.description,
                    timestamp: new Date().toISOString(),
                    type: 'user',
                    bubbleColor: pickChatBubbleColor(action.char_id),
                  });
                }
              }

              // 2b. Private messages (whispers TO this player's character)
              // Skip if a preview whisper is already REVEALED for this round —
              // re-enqueueing on top of it would double-post. (Still-queued
              // preview whispers were already filtered out of messageQueue by
              // the first set() above via isStalePreview, so this guard only
              // needs to inspect `messages`.)
              const hasWhisperPreview = currentState.messages.some((m) =>
                m.id.startsWith(`whisper-preview-${resultEvent.round}-`)
              );
              if (
                !hasWhisperPreview &&
                resultEvent.my_private_messages &&
                resultEvent.my_private_messages.length > 0
              ) {
                for (const [idx, pm] of resultEvent.my_private_messages.entries()) {
                  const senderInfo =
                    partyMap[pm.from_char_id] ??
                    currentState.sceneCharacters.find((c) => c.charId === pm.from_char_id);
                  queuedMessages.push({
                    id: `whisper-${resultEvent.round}-${pm.from_char_id}-${idx}`,
                    senderId: pm.from_char_id,
                    senderName: senderInfo?.name ?? pm.from_char_name,
                    avatarUrl: pm.avatar_url ?? senderInfo?.avatarUrl,
                    avatarEmoji: pm.avatar_emoji ?? senderInfo?.avatarEmoji,
                    senderPortraitUrl: pm.portrait_url ?? senderInfo?.portraitUrl,
                    content: pm.message,
                    timestamp: new Date().toISOString(),
                    type: 'whisper',
                    bubbleColor: pickChatBubbleColor(pm.from_char_id),
                  });
                }
              }

              // 2c. Private narrative (my_char_narrative) as whisper from player's own character.
              // Skip if a private-narrative preview is already REVEALED for this round.
              // Exact match — the preview id is stable `private-narrative-preview-${round}`
              // (one per round, no timestamp suffix). Still-queued preview was already
              // filtered out by the first set() via isStalePreview, so this guard only
              // needs to inspect `messages`.
              const hasPrivateNarrativePreview = currentState.messages.some(
                (m) => m.id === `private-narrative-preview-${resultEvent.round}`
              );
              if (resultEvent.my_char_narrative && !hasPrivateNarrativePreview) {
                const myCharId = currentState.playerCharacterId;
                const playerChar = myCharId ? partyMap[myCharId] : undefined;
                queuedMessages.push({
                  id: `private-narrative-${resultEvent.round}`,
                  senderId: myCharId ?? 'self',
                  senderName: playerChar?.name ?? 'You',
                  avatarUrl: playerChar?.avatarUrl,
                  avatarEmoji: playerChar?.avatarEmoji,
                  senderPortraitUrl: playerChar?.portraitUrl,
                  content: resultEvent.my_char_narrative,
                  timestamp: new Date().toISOString(),
                  type: 'whisper',
                  bubbleColor: pickChatBubbleColor(myCharId ?? 'self'),
                });
              }

              // 2d. Rejected intents — show as bubble from the player's own character.
              //
              // Composition rules (player-facing, ordered by friendliness):
              //   1. If the BE provided a `reason` that's already a complete
              //      narrative sentence (ends with .!?), surface it AS the
              //      message body — leading with the reason puts the
              //      actionable info at the top instead of buried under a
              //      generic prefix. Example: rest-blocked-in-lair returns
              //      "The air hums with danger — this is no place to let
              //      your guard down." — that line stands alone as a DM
              //      narration.
              //   2. If a `suggestion` is provided (engine telling the
              //      player what to try instead), the generic
              //      "*That didn't quite work*" softener helps frame the
              //      suggestion. Suggestion appears below.
              //   3. Fallback: just the softener.
              //
              // Rejected intents are rendered from STRUCTURED fields only —
              // never from `dev_reason` (which may contain enum syntax,
              // internal IDs, or routing hints). Composition rule:
              //   classified  → "Failed to {verb} {object}{ on {subject}}{ — {reason}}."
              //   unclassified → "*That didn't quite work. 😕*"
              // Every rejection is also `console.warn`-logged with the full
              // structured payload so engineers can correlate to backend
              // logs without combing staging.
              if (resultEvent.my_rejected_intents && resultEvent.my_rejected_intents.length > 0) {
                const myCharId = currentState.playerCharacterId;
                const playerChar = myCharId ? partyMap[myCharId] : undefined;
                for (const [idx, ri] of resultEvent.my_rejected_intents.entries()) {
                  // Always log the full payload — UI rendering is lossy by design.
                  console.warn('rejected_intent', ri);
                  const content = composeRejectionMessage(ri);
                  queuedMessages.push({
                    id: `rejected-${resultEvent.round}-${idx}`,
                    senderId: myCharId ?? 'self',
                    senderName: playerChar?.name ?? 'You',
                    avatarUrl: playerChar?.avatarUrl,
                    avatarEmoji: playerChar?.avatarEmoji,
                    senderPortraitUrl: playerChar?.portraitUrl,
                    content,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                    bubbleColor: pickChatBubbleColor(myCharId ?? 'self'),
                  });
                }
              }

              // 2e. DM narrative (last — the climax)
              // When preview was streamed, resultEvent.narrative contains ONLY post-round additions
              // (ambient damage, detection text, XP) — never a duplicate of the preview stream.
              // When no preview, it contains the full LLM narrative. Always show if non-empty.
              //
              // #347 safety net: a backend preview/result desync (preview-forwarder ordering race,
              // dropped preview on a full reply channel, or a reconnect wiping preview_sent_to) can
              // deliver the FULL narrative here even though the same-round preview/stream bubble
              // already rendered it. Those bubbles use different round-keyed ids
              // (dm-narrative-preview-${round} / dm-narrative-stream-${round}), so enqueueMessages'
              // id-dedup can't collapse them, and the preview bubble is intentionally kept in
              // `messages` (see the 2004-2009 comment). Skip the duplicate when an existing
              // same-round DM bubble already holds this exact (whitespace-normalized) narrative.
              if (resultEvent.narrative) {
                const normalizeNarrative = (s: string) => s.replace(/\s+/g, ' ').trim();
                const incomingNarrative = normalizeNarrative(resultEvent.narrative);
                // Scan BOTH messages and the still-draining messageQueue: a
                // queued-but-unrevealed dm-narrative-preview is now KEPT (see the
                // messageQueue filter above), so on a desync that re-delivers the
                // SAME full narrative we must dedup against the queued copy too,
                // not just what already rendered into `messages`.
                const alreadyRenderedThisRound = [
                  ...currentState.messages,
                  ...currentState.messageQueue,
                ].some(
                  (m) =>
                    (m.id === `dm-narrative-preview-${resultEvent.round}` ||
                      m.id === `dm-narrative-stream-${resultEvent.round}`) &&
                    normalizeNarrative(m.content) === incomingNarrative
                );
                if (!alreadyRenderedThisRound) {
                  queuedMessages.push({
                    id: `dm-narrative-${resultEvent.round}`,
                    senderId: GAME_MASTER.charId,
                    senderName: GAME_MASTER.name,
                    avatarUrl: GAME_MASTER.avatarUrl,
                    avatarEmoji: GAME_MASTER.avatarEmoji,
                    content: resultEvent.narrative,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                  });
                }
              }

              // 2f. Encounter media — show encounter video/image when living hostiles are present.
              // Triggers when hostiles first appear (roleplay encounter) OR combat starts.
              // Dedup: skip if a combat-encounter image was already shown recently.
              {
                const changes = resultEvent.session_changes;
                const freshState = get();
                const nowHasLivingHostiles = freshState.sceneCharacters.some(
                  (c) =>
                    c.healthState !== 'dead' && (c.type === 'monster' || c.attitude === 'hostile')
                );
                const hostilesAppeared = !prevHadLivingHostiles && nowHasLivingHostiles;
                const recentCombatImageShown = [
                  ...freshState.messages,
                  ...freshState.messageQueue,
                ].some(
                  (m) =>
                    m.id.startsWith('combat-encounter-') &&
                    Date.now() - new Date(m.timestamp).getTime() < 10000
                );
                if (
                  (changes?.combatTrigger || hostilesAppeared) &&
                  (freshState.currentScene?.encounterVideoUrl ||
                    freshState.currentScene?.encounterImageUrl) &&
                  !recentCombatImageShown
                ) {
                  queuedMessages.unshift(
                    buildSceneImageMessage(
                      freshState.currentScene,
                      `combat-encounter-${resultEvent.round}`,
                      { preferEncounter: true, includeDescription: false }
                    )
                  );
                }
              }

              // 2g. Encounter resolved — show base scene image when hostiles are gone,
              // so the chat no longer shows the encounter (monster) image.
              // Triggers when all hostiles die (combat or otherwise) OR formal combat resolution.
              {
                const changes = resultEvent.session_changes;
                const freshState = get();
                const nowHasLivingHostiles = freshState.sceneCharacters.some(
                  (c) =>
                    c.healthState !== 'dead' && (c.type === 'monster' || c.attitude === 'hostile')
                );
                const hostilesCleared = prevHadLivingHostiles && !nowHasLivingHostiles;
                const recentResolutionImageShown = [
                  ...freshState.messages,
                  ...freshState.messageQueue,
                ].some(
                  (m) =>
                    m.id.startsWith('combat-resolved-') &&
                    Date.now() - new Date(m.timestamp).getTime() < 10000
                );
                if (
                  (changes?.combatResolution || hostilesCleared) &&
                  (freshState.currentScene?.encounterVideoUrl ||
                    freshState.currentScene?.encounterImageUrl) &&
                  !recentResolutionImageShown
                ) {
                  queuedMessages.push(
                    buildSceneImageMessage(
                      freshState.currentScene,
                      `combat-resolved-${resultEvent.round}`,
                      { preferEncounter: false, includeDescription: false }
                    )
                  );
                }
              }

              // Enqueue all collected messages for progressive reveal.
              // Use get() for fresh reference after preceding set() calls.
              if (queuedMessages.length > 0) {
                get().enqueueMessages(queuedMessages);
              }

              // 3. Handle session changes IMMEDIATELY (toasts, not queued)
              const sessionChanges = resultEvent.session_changes;
              if (sessionChanges) {
                if (sessionChanges.combatResolution) {
                  const resolutionMessages: Record<string, string> = {
                    victory: 'Victory! Combat has ended.',
                    retreat: 'The party has retreated from combat.',
                    negotiate: 'Combat resolved through negotiation.',
                    fled: 'The enemies have fled!',
                  };
                  const message = resolutionMessages[sessionChanges.combatResolution];
                  if (message) {
                    toast.info(message, { duration: 4000 });
                  }
                }
                if (sessionChanges.combatTrigger) {
                  toast.warning('Combat initiated!', {
                    description: sessionChanges.combatTrigger,
                    duration: 4000,
                  });
                }
                if (sessionChanges.restCompleted) {
                  const restMessages: Record<string, string> = {
                    short: 'The party completed a short rest.',
                    long: 'The party completed a long rest. Full recovery!',
                  };
                  const message = restMessages[sessionChanges.restCompleted];
                  if (message) {
                    toast.success(message, { duration: 4000 });
                  }
                }
              }
              break;
            }

            case 'chat_broadcast': {
              // Skip if this is an echo of our own message (already added optimistically)
              const currentCharId = get().playerCharacterId;
              if (currentCharId && event.char_id && event.char_id === currentCharId) {
                break;
              }
              // O(1) lookup from partyMembersMap for avatar
              const partyMap = state.partyMembersMap;
              const charInfo = event.char_id ? partyMap[event.char_id] : undefined;
              state.addMessage({
                id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                senderId: event.char_id,
                senderName: charInfo?.name ?? event.char_name,
                avatarUrl: charInfo?.avatarUrl,
                avatarEmoji: charInfo?.avatarEmoji,
                senderPortraitUrl: charInfo?.portraitUrl,
                content: event.message,
                timestamp: new Date().toISOString(),
                type: event.char_name === 'System' ? 'system' : 'user',
                bubbleColor: event.char_id ? pickChatBubbleColor(event.char_id) : undefined,
              });
              // Polymyth 2.0: party chat also lands as a 3D bubble over the
              // speaker (system lines are announcements, not speech).
              if (event.char_id && event.char_name !== 'System') {
                get().queueWorldSpeech([{ charId: event.char_id, text: event.message }]);
              }
              break;
            }

            case 'round_started': {
              // Backend emits this at the top of `spawn_round_execution` and
              // to late joiners during warmup. It flips the thinking indicator
              // on BEFORE game_state arrives, so during the warmup window the
              // user sees orange dots instead of a silent loading screen.
              //
              // We deliberately do NOT touch `sessionStartRound` here — the
              // `game_state` handler arms it on drain (gated on `warmupActive`
              // from `warmup_holding`). This way the drain replay guard in
              // `narrative_stream` / `narrative_preview` / `round_result` keeps
              // the indicator up through the warmup replay without interference.
              set({ isWaitingForRound: true });
              break;
            }

            case 'warmup_holding': {
              // Connection accepted; `game_state` and this round's narrative are
              // buffered until the warmup gate releases. This message is also the
              // server's explicit "I'm alive" signal — without it the client sees
              // silence and its watchdog can fire a spurious reconnect that races
              // session creation.
              //
              // Flag the warmup as active. warmup_holding is the definitive
              // warmup signal: the backend sends it on any connection taken
              // while the warmup buffer is active (so never on a resume, but
              // yes on a reconnect during a fresh warmup), and always before the
              // drained game_state, which consumes the flag to arm the
              // warmup-replay gate (`sessionStartRound`) from its currentRound,
              // the correct drain boundary in every release mode (this message's
              // `round` is 1 at connect even when the drain carries R1+R2). A
              // resumed session gets no warmup_holding, so its single live
              // kickstart round clears the indicator normally (#35).
              console.debug('[gameStore] warmup_holding received, round:', event.round);
              set({ warmupActive: true, isWaitingForRound: true });
              break;
            }

            case 'intent_rejected': {
              // Out-of-round rejection delivery (backend wait-for-player-intent
              // test mode): a rejected input fired no round, so the rejection
              // arrives standalone instead of riding round_result. Same
              // rendering contract as round_result's my_rejected_intents:
              // structured fields only, dev_reason to console only.
              //
              // Clear the thinking indicator: the submit set it, and no round
              // (and thus no round_result) will arrive to clear it.
              //
              // Roll the optimistic decrement back from the pre-tap snapshot.
              // Every other rejection rides a round_result and is reconciled by
              // the authoritative `my_character_session`; this path has no
              // round, so without the restore a rejected card stayed greyed out
              // until the next snapshot or a reconnect.
              set((prev) => {
                const restore = prev.optimisticRestore;
                if (!restore) return { isWaitingForRound: false, optimisticRestore: null };
                const put = (c: GameCharacterInfo) =>
                  c.charId === restore.charId ? restore.char : c;
                return {
                  isWaitingForRound: false,
                  optimisticRestore: null,
                  partyMembers: prev.partyMembers.map(put),
                  sceneCharacters: prev.sceneCharacters.map(put),
                  partyMembersMap: prev.partyMembersMap[restore.charId]
                    ? { ...prev.partyMembersMap, [restore.charId]: restore.char }
                    : prev.partyMembersMap,
                };
              });
              const rejState = get();
              const rejCharId = rejState.playerCharacterId;
              const rejChar = rejCharId ? rejState.partyMembersMap[rejCharId] : undefined;
              for (const [idx, ri] of event.my_rejected_intents.entries()) {
                console.warn('rejected_intent (out-of-round)', ri);
                state.addMessage({
                  id: `rejected-oob-${Date.now()}-${idx}`,
                  senderId: rejCharId ?? 'self',
                  senderName: rejChar?.name ?? 'You',
                  avatarUrl: rejChar?.avatarUrl,
                  avatarEmoji: rejChar?.avatarEmoji,
                  senderPortraitUrl: rejChar?.portraitUrl,
                  content: composeRejectionMessage(ri),
                  timestamp: new Date().toISOString(),
                  type: 'dm',
                  bubbleColor: pickChatBubbleColor(rejCharId ?? 'self'),
                });
              }
              break;
            }

            case 'round_failed': {
              // Explicit round failure — retract phantom preview messages using exact round number
              console.error('Round failed:', event.round, event.message);
              // Intentionally do NOT clear `suppressStreamAfterSceneVideo` here:
              // the server replays this same round (re-streaming its narrative
              // without re-broadcasting scene_transition), so the retry must stay
              // suppressed beside the still-mounted scene video (BUG-6). It lifts
              // when the retry's narration anchors (preview/round_result).
              //
              // DO disarm the warmup-replay gate: on a warmup failure the
              // backend force-releases the gate (draining the buffer, whose
              // game_state armed `sessionStartRound`) BEFORE broadcasting this
              // failure, and then RETRIES the same round number live. An armed
              // gate would misclassify that retry as buffered replay
              // (round <= sessionStartRound) and never clear the indicator.
              //
              // Same story for the 3D presentation watermark: the force-release
              // game_state advanced it to currentRound even though the failed
              // round never applied. Roll back to the failed round's
              // predecessor so the live retry's transcript plays (no-op when
              // the watermark is already behind it).
              set((prev) => ({
                isWaitingForRound: false,
                streamingNarrative: null,
                streamingNarrativeRound: null,
                sessionStartRound: null,
                warmupActive: false,
                lastPlayedPresentationRound: rollbackPresentationWatermarkForFailedRound(
                  prev.lastPlayedPresentationRound,
                  event.round
                ),
              }));
              get().removePreviewMessages(event.round);
              // The world layer holds the same lines: parked bubbles for
              // speakers that had not mounted, plus the dedupe claim the
              // retry needs released to say something different.
              get().retractWorldSpeechForRound(event.round);
              state.addMessage({
                id: `err-${Date.now()}`,
                senderName: 'System',
                content:
                  'Something went wrong — your actions are preserved and the round is retrying automatically.',
                timestamp: new Date().toISOString(),
                type: 'error',
              });
              break;
            }

            case 'error': {
              // Technical failure from the server — log structured details so
              // DevTools + session logs can always reconstruct what happened.
              // The UI shows a generic fallback; the real text goes to console
              // (and the server already logs it at WARN via broadcast_message).
              console.error(
                '[GameServerError]',
                JSON.stringify({
                  technicalMessage: event.message,
                  timestamp: new Date().toISOString(),
                  streamingRound: get().streamingRound,
                })
              );
              // Like round_failed, leave `suppressStreamAfterSceneVideo` intact —
              // a recoverable round error replays the same round next to the
              // still-mounted scene video; suppression lifts on the next anchor.
              set({ isWaitingForRound: false });

              // Fallback cleanup: retract ghost preview messages if streamingRound is set
              // (handles non-round-failure errors that arrive during preview phase)
              const failedRound = get().streamingRound;
              if (failedRound !== null) {
                get().removePreviewMessages(failedRound);
                // A recoverable error replays the same round, so its
                // speech needs the same retraction round_failed does.
                get().retractWorldSpeechForRound(failedRound);
              }

              state.addMessage({
                id: `err-${Date.now()}`,
                senderName: 'System',
                content: 'Sorry, the portal is experiencing some glitches. Please try again later.',
                timestamp: new Date().toISOString(),
                type: 'error',
              });
              break;
            }

            case 'system_notification': {
              // Informational lifecycle event (leader disconnect/reconnect,
              // player left, etc.). Shown as a plain system message in chat.
              // NOT an error — this is the variant that was previously bolted
              // onto the Error type and mis-rendered as "portal glitches".
              console.info('[GameServer system_notification]', event.message);
              state.addMessage({
                id: `sys-${Date.now()}`,
                senderName: 'System',
                content: event.message,
                timestamp: new Date().toISOString(),
                type: 'system',
              });
              break;
            }

            case 'session_ending': {
              // Training duels: keep the player on the game UI (status stays
              // 'connected') and post a Master Kira placeholder while the LLM
              // generates the recap + coaching. Status only flips to 'ending'
              // when the player explicitly types /end-session — anything
              // earlier yanks them off the screen mid-read.
              //
              // Non-duel sessions: show the standard "session ending" banner
              // immediately; recap is delivered via Tavian in lobby chat after
              // navigation, so we don't need to keep the player here.
              //
              // `isTrainingDuel` is sourced from the WS event itself — the
              // server is authoritative about session type. Don't infer from
              // `game-session-duel-portraits` localStorage (that flag is a
              // portrait-loader hint and gets cleared on unrelated paths,
              // which used to misclassify duel-ends as regular adventures and
              // skip Kira's recap window).
              const isDuelEnding = event.isTrainingDuel === true;
              set({ sessionEndIsDuel: isDuelEnding });
              if (isDuelEnding) {
                state.addMessage({
                  id: DUEL_COACH_PLACEHOLDER_ID,
                  senderId: MASTER_KIRA.charId,
                  senderName: MASTER_KIRA.name,
                  avatarUrl: MASTER_KIRA.avatarUrl,
                  avatarEmoji: MASTER_KIRA.avatarEmoji,
                  content:
                    '⚔️ *Wait a few seconds — reviewing your duel performance and generating recommendations...*',
                  timestamp: new Date().toISOString(),
                  type: 'dm',
                });
              } else {
                set({ connectionStatus: { status: 'ending', reason: event.reason } });
              }
              break;
            }

            case 'session_ended': {
              // Clean up any preview messages from a round in flight when session ended
              const endingRound = get().streamingRound;
              if (endingRound !== null) {
                get().removePreviewMessages(endingRound);
                get().retractWorldSpeechForRound(endingRound);
              }
              // `isTrainingDuel` is sourced from the WS event — server is
              // authoritative. See session_ending case above for why we no
              // longer infer this from the duel-portraits localStorage hint.
              // Both fields land in one atomic `set()`; the JS event loop
              // guarantees this `onmessage` handler runs to completion before
              // the 4100 `onclose` fires, so the close-handler always reads
              // both fields populated.
              const isDuel = event.isTrainingDuel === true;
              set({
                sessionEndSummary: event.summary ?? null,
                sessionEndIsDuel: isDuel,
                isWaitingForRound: false,
              });
              if (typeof localStorage !== 'undefined') {
                // These are DIRECT localStorage writes (they bypass the
                // fail-soft persist adapter), so guard them here too: a
                // QuotaExceeded throw must never abort this handler before it
                // reaches `status: 'ended'` below (#349).
                try {
                  localStorage.removeItem('active-game-session');
                  localStorage.removeItem('game-session-party');
                  // Only persist recap for adventures — duel recaps are shown inline
                  // and should not appear as "Previously..." in the next adventure
                  if (event.summary && !isDuel) {
                    localStorage.setItem('game-session-last-summary', event.summary);
                  } else if (isDuel) {
                    // Defensive cleanup: a previous wire-format bug
                    // (SessionEnding.is_training_duel serialized as snake_case
                    // before the camelCase rename in 719eb3771) caused the
                    // !isDuel branch above to fire on every duel-end, persisting
                    // duel recaps as `game-session-last-summary` and surfacing
                    // them as the "Previously..." block on the next Goldvale
                    // start. Drop the key on every duel-end so any pre-fix
                    // stale entry is wiped and no future regression can leak
                    // a duel recap into adventure recap UI. See issue #230.
                    localStorage.removeItem('game-session-last-summary');
                  }
                  // Carry final game time to lobby for immediate ModulePanel display
                  // (avoids race where player reaches lobby before game-server POSTs summary)
                  if (event.finalGameTimeSeconds != null && event.partyId) {
                    localStorage.setItem(
                      `game-session-final-time:${event.partyId}`,
                      String(event.finalGameTimeSeconds)
                    );
                  }
                } catch (e) {
                  console.warn('[gameStore] session_ended localStorage write failed', e);
                }
              }

              // Training duels: show the sports-style recap in game chat and delay
              // the transition so the user can read it before navigating to lobby.
              // Normal sessions: recap is delivered via Tavian in lobby party chat,
              // so skip the game chat message and transition immediately.

              // Always clear Kira's "reviewing..." placeholder on duel end —
              // even when the summary is missing (LLM error). Otherwise the
              // bubble persists in zustand's persisted message log.
              if (isDuel) {
                set((prev) => ({
                  messages: prev.messages.filter((m) => m.id !== DUEL_COACH_PLACEHOLDER_ID),
                }));
              }

              if (isDuel && event.summary) {
                const markerIdx = event.summary.indexOf(DUEL_COACHING_MARKER);
                const recapText =
                  markerIdx >= 0 ? event.summary.slice(0, markerIdx).trim() : event.summary;
                const coachingText = markerIdx >= 0 ? event.summary.slice(markerIdx).trim() : null;

                if (recapText) {
                  state.addMessage({
                    id: `session-end-${Date.now()}`,
                    senderId: GAME_MASTER.charId,
                    senderName: GAME_MASTER.name,
                    avatarUrl: GAME_MASTER.avatarUrl,
                    avatarEmoji: GAME_MASTER.avatarEmoji,
                    content: `⚔️ ${recapText}`,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                  });
                }
                if (coachingText) {
                  state.addMessage({
                    id: `session-end-coaching-${Date.now()}`,
                    senderId: MASTER_KIRA.charId,
                    senderName: MASTER_KIRA.name,
                    avatarUrl: MASTER_KIRA.avatarUrl,
                    avatarEmoji: MASTER_KIRA.avatarEmoji,
                    content: coachingText,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                  });
                }
                state.addMessage({
                  id: `session-end-hint-${Date.now()}`,
                  senderId: MASTER_KIRA.charId,
                  senderName: MASTER_KIRA.name,
                  avatarUrl: MASTER_KIRA.avatarUrl,
                  avatarEmoji: MASTER_KIRA.avatarEmoji,
                  content:
                    "Take your time reading. When you're ready to head back to the hilltop, just type `end session`.",
                  timestamp: new Date().toISOString(),
                  type: 'dm',
                });
                // Don't flip connectionStatus — status stays 'connected' so
                // the game UI keeps rendering and there's no "session ending"
                // banner. The player chooses when to leave via /end-session
                // (intercepted in ChatPanel: when sessionEndSummary is set,
                // it navigates client-side instead of going through the dead WS).
              } else {
                // Normal session or no summary — transition immediately
                if (event.summary) {
                  // Prepend celebration video for module completion
                  const isModuleCompleted = event.reason === 'module_completed';
                  const heroVideo =
                    isModuleCompleted && typeof localStorage !== 'undefined'
                      ? localStorage.getItem('game-session-hero-video')
                      : null;
                  const celebrationPrefix = heroVideo ? `[MODULE_IMAGE:${heroVideo}]\n\n` : '';
                  state.addMessage({
                    id: `session-end-${Date.now()}`,
                    senderId: GAME_MASTER.charId,
                    senderName: GAME_MASTER.name,
                    avatarUrl: GAME_MASTER.avatarUrl,
                    avatarEmoji: GAME_MASTER.avatarEmoji,
                    content: `${celebrationPrefix}${event.summary}`,
                    timestamp: new Date().toISOString(),
                    type: 'dm',
                  });
                }
                set({ connectionStatus: { status: 'ended', reason: event.reason } });
              }
              // Browser notification if tab not focused
              if (typeof window !== 'undefined' && !document.hasFocus()) {
                notificationService.notify('Game Session Ended', {
                  body: event.summary ?? `Session ended: ${event.reason}`,
                  type: 'message',
                });
              }
              break;
            }

            case 'missed_rounds': {
              // Clean up stale preview messages from before disconnection
              const staleRound = get().streamingRound;
              if (staleRound !== null) {
                get().removePreviewMessages(staleRound);
                // The in-flight round never played as previewed; its
                // parked lines must not surface after the catch-up.
                get().retractWorldSpeechForRound(staleRound);
              }
              // Handle reconnection - catch up on missed rounds
              console.log('[GameStore] Processing missed rounds', {
                disconnectedAt: event.disconnected_at_round,
                current: event.current_round,
                count: event.rounds.length,
              });
              // Update current round. The missed narratives themselves are NOT
              // replayed into state: the old narrative/narrativeLog arrays had
              // no reader anywhere in the UI and re-appended the full replayed
              // history on every reconnect, growing without bound (the chat
              // transcript is rebuilt separately from game_state + messages).
              set({ currentRound: event.current_round });
              // Toast notification
              toast.info(`Caught up on ${event.rounds.length} missed round(s)`, {
                description: `Reconnected from round ${event.disconnected_at_round} to ${event.current_round}`,
              });
              break;
            }

            case 'action_queued':
              // Provide feedback that action was queued
              console.log('[GameStore] Action queued', {
                characterId: event.characterId,
                action: event.action,
              });
              toast.info('Action queued', {
                description: event.action,
                duration: 2000,
              });
              break;

            case 'player_ready':
              // Player readied up - log for debugging
              console.log('[GameStore] Player ready', {
                userId: event.userId,
                characterId: event.characterId,
                characterName: event.characterName,
                ready: event.ready,
              });
              break;

            case 'all_players_ready':
              // All players are ready - game can proceed
              console.log('[GameStore] All players ready', {
                playerCount: event.playerCount,
              });
              toast.success('All players ready!', {
                description: `${event.playerCount} player(s) connected`,
                duration: 3000,
              });
              break;

            case 'player_disconnected': {
              // Player disconnected - show notification
              const dcCharName = event.characterName ?? 'A player';
              console.log('[GameStore] Player disconnected', {
                userId: event.userId,
                characterId: event.characterId,
                gracePeriodMs: event.gracePeriodMs,
              });
              toast.warning(`${dcCharName} disconnected`, {
                description: `Grace period: ${Math.round(event.gracePeriodMs / 1000)}s`,
                duration: 4000,
              });
              break;
            }

            case 'player_reconnected': {
              // Player reconnected
              const rcCharName = event.characterName ?? 'A player';
              console.log('[GameStore] Player reconnected', {
                userId: event.userId,
                characterId: event.characterId,
              });
              toast.success(`${rcCharName} reconnected`);
              break;
            }

            case 'player_away': {
              // Away-status flip (grace expired / player returned): update the
              // roster flag; the chat notice arrives via system_notification.
              console.log('[GameStore] Player away-status change', {
                characterId: event.characterId,
                away: event.away,
              });
              set((state) => {
                const setAway = (c: GameCharacterInfo): GameCharacterInfo =>
                  c.charId === event.characterId ? { ...c, away: event.away } : c;
                const partyMembers = state.partyMembers.map(setAway);
                const sceneCharacters = state.sceneCharacters.map(setAway);
                const partyMembersMap = { ...state.partyMembersMap };
                if (partyMembersMap[event.characterId]) {
                  partyMembersMap[event.characterId] = setAway(partyMembersMap[event.characterId]);
                }
                return { partyMembers, sceneCharacters, partyMembersMap };
              });
              break;
            }

            // Movement messages (handlers ready for when backend implements them)
            case 'party_positions': {
              // Pre-combat 3D repositions (engine-validated + persisted).
              // Patch the affected characters' session positions and refresh
              // the renderer snapshot so every client's board follows.
              const moved = new Map(
                (event.positions ?? []).map((p: { charId: string; x: number; y: number }) => [
                  p.charId,
                  p,
                ])
              );
              if (moved.size === 0) break;
              set((prev) => {
                const patch = (char: GameCharacterInfo): GameCharacterInfo => {
                  const pos = moved.get(char.charId);
                  if (!pos) return char;
                  const session = {
                    ...((char.session ?? {}) as Record<string, unknown>),
                    position: { x: pos.x, y: pos.y },
                  } as GameCharacterInfo['session'];
                  return { ...char, session };
                };
                const characters = prev.characters.map(patch);
                return {
                  characters,
                  partyMembers: prev.partyMembers.map(patch),
                  sceneCharacters: prev.sceneCharacters.map(patch),
                  worldEntitySnapshot: worldSnapshotFromGameState(characters),
                };
              });
              break;
            }

            case 'map_update':
              console.log('[GameStore] Map update', event);
              set({
                currentLocationId: event.currentLocationId,
                discoveredLocations: event.discoveredLocations,
                discoveredScenes: event.discoveredScenes,
                mapImageUrl: event.mapImage ?? null,
                // mapVideoUrl intentionally not updated — map_update protocol has no video field
              });
              break;

            case 'movement_proposed':
              console.log('[GameStore] Movement proposed', event);
              // TODO: Show vote UI when movement UI is implemented
              break;

            case 'movement_vote_cast':
              console.log('[GameStore] Movement vote cast', event);
              // TODO: Update vote counts in UI when implemented
              break;

            case 'movement_started':
              console.log('[GameStore] Movement started', event);
              // TODO: Show travel animation when UI is implemented
              toast.info(`Traveling to ${event.targetName}`, {
                description: event.isTravel
                  ? `Travel time: ${event.travelMinutes} minutes`
                  : undefined,
              });
              break;

            case 'movement_complete':
              console.log('[GameStore] Movement complete', event);
              // Update current scene; clear scene chars from prior scene
              // (next round_result repopulates from observable views — same
              // rationale as scene_transition above).
              set({
                currentScene: {
                  sceneId: event.sceneId,
                  name: event.sceneName,
                  description: event.description,
                },
                currentSceneId: event.sceneId,
                sceneCharacters: [],
                // 3D world: same hard-cancel rationale as scene_transition.
                pendingPresentationRound: null,
                worldEntitySnapshot: null,
              });
              break;

            case 'movement_blocked':
              console.log('[GameStore] Movement blocked', event);
              toast.warning('Movement blocked', {
                description: event.hint ?? event.reason,
              });
              break;

            default:
              // Unhandled game event - silently ignore
              break;
          }
        },

        markPresentationRoundPlayed: (roundId) =>
          set((state) => ({
            lastPlayedPresentationRound: advancePresentationWatermark(
              state.lastPlayedPresentationRound,
              roundId
            ),
            pendingPresentationRound:
              state.pendingPresentationRound !== null &&
              state.pendingPresentationRound.round_id <= roundId
                ? null
                : state.pendingPresentationRound,
          })),

        reset: () => {
          const { _recapTimerId } = get();
          if (_recapTimerId !== null) clearTimeout(_recapTimerId);
          // Speech dedup ring is per session: a fresh session restarts
          // rounds, so its stable action ids repeat old shapes and would
          // otherwise be swallowed. (worldSpeechSeq stays monotonic: a
          // consumer ref may outlive the reset by a frame.)
          worldSpeechSeenIds.clear();
          worldSpeechSeenOrder.length = 0;
          // In-memory store reset only. localStorage cleanup is the caller's
          // responsibility — `clearGameSessionState('all')` handles terminal
          // paths via ROUTE_HINT_KEYS + FULL_RESET_KEYS, and SIGNED_OUT_ELSEWHERE
          // clears explicitly. Wiping localStorage from here also fired during
          // GameConnectionManager's session-switch reset (back-to-back duels),
          // clobbering portraits/hero data that LobbyLayout had just written
          // for the new session — which surfaced as a black duel loader.
          set(initialState);
        },
      }),
      {
        name: 'game-storage',
        version: 2,
        storage: createJSONStorage(() => failSoftStorage),
        partialize: (state) => ({
          // `token-gate` bubbles are ephemeral (in-chat low-balance hint that
          // ages out as soon as the balance changes). Persisting them resurrects
          // a stale "out of Polymyth Tokens" message after the player tops up
          // and reopens the app — filter them out before serialization.
          messages: capMessages(state.messages)
            .map(({ isStreaming: _, isQueuedReveal: __, ...rest }) => rest)
            .filter((m) => m.content?.trim() && m.type !== 'token-gate'),
        }),
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as Record<string, unknown>;
          if (version < 1) {
            // v0 persisted gameSessionId which leaked game mode into lobby ChatPanel
            delete state.gameSessionId;
          }
          if (version < 2) {
            // BUG-6: builds before the message cap persisted an unbounded
            // `messages` array. partialize only clamps on WRITE, so without a
            // read-side trim an already-affected user rehydrates the huge array
            // and re-OOMs the renderer before the first capped write lands.
            if (Array.isArray(state.messages) && state.messages.length > MAX_RENDERED_MESSAGES) {
              state.messages = state.messages.slice(state.messages.length - MAX_RENDERED_MESSAGES);
            }
          }
          return state as Partial<GameState>;
        },
      }
    )
  )
);
